import type { Dictionary } from '@reduxjs/toolkit';
import {
	createAsyncThunk,
	createSelector,
	createSlice,
} from '@reduxjs/toolkit';
import { remove } from 'lodash';
import { goBack, push } from 'react-router-redux';
import { Customer, LocalStorage, ManageMetaProducts } from '~/helpers';
import { route } from '~/helpers/Routes';

import type { IAppState } from '~/appstate';
import type { IBlueprint, ICustomCategory, ISupplier } from '~/domain';
import { AvailabilityResultState } from '~/domain';
import { ApiRequestError, ApiServerError, fetch2, unwrapFetch2 } from '~/fetch';
import { endpoint } from '~/helpers/Endpoints';
import type { IToastMessage } from '~/helpers/Toastr';
import { toastr } from '~/helpers/Toastr';
import * as User from '~/helpers/User';
import type {
	DistributionCenterWithUMs,
	GetUSFoodsProductAttributesArgs,
	MetaProductGroupingSummaryLine,
	ProductHeaderPrices,
	ProductNetUnits,
	SearchMetaProductGroupingArgsV6,
	USFoodsProductAttributes,
} from '~/ogm-hub';
import * as Hub from '~/ogm-hub';
import {
	MetaProductController,
	MetaProductGroupingSource,
	MetaProductGroupingStatus,
} from '~/ogm-hub';
import type { SelectionRuleSet } from '~/remote/hubs/Web_Controllers_V2_CustomersController';
import * as addBySupcModalSlice from '~/Slices/AddBySupcModalSlice';
import { fetchBlueprint, selectBlueprintById } from '~/Slices/BlueprintsSlice';
import { fetchDistributionCentersForBlueprint } from '~/Slices/DistributionCentersSlice';
import * as metaProductIsSelectByUmWarningModalSlice from '~/Slices/MetaProductIsSelectByUmSettingWarningModalSlice';
import {
	deleteMetaProduct,
	fetchMetaProduct,
	saveMetaProduct,
} from '~/Slices/MetaProductsSlice';
import * as SuggestionsSlice from '~/Slices/OGMAI/SuggestionsSlice';
import { fetchProductAvailabilities } from '~/Slices/ProductAvailabilitiesSlice';
import {
	fetchHeaderLevelBlueprintPrices,
	selectProductHeaderPrice,
} from '~/Slices/ProductHeaderPricesSlice';
import type {
	FetchRecommendationsParams,
	FetchSearchResults,
	FetchSearchResultsParams,
	IFetchGLGroupsForProductParms,
} from '~/Slices/ProductHeadersSlice';
import {
	fetchGLGroups,
	fetchProductsByProductNumber,
	fetchRecommendations,
	fetchSearchResults,
	selectProductHeader,
	selectProductHeaders,
	sortPackSizeFacet,
} from '~/Slices/ProductHeadersSlice';
import {
	fetchLineLevelPrices,
	fetchMultipleLineLevelPrices,
	selectProductLinePrices,
} from '~/Slices/ProductLinePricesSlice';
import {
	createProductLineId,
	fetchProductLines,
} from '~/Slices/ProductLinesSlice';
import { getProductUsageHistory } from '~/Slices/ProductUsageHistorySlice';
import type {
	IDistributionCenter,
	IMetaProduct,
	IMetaProductDistributionCenterOverride,
	IProduct,
	IProductCoverage,
	IProductHeader,
	IProductWithNetUnits,
} from '~/Slices/schema';
import {
	AvailableMaintainedByTypes,
	AvailableSpecIndicatorTypes,
	GridViewDisplayType,
	IMaintainedByType,
	IMetaProductDistributionCenterOverrideType,
	IMetaProductStatus,
	LoadingStatus,
	OGMAIViewTab,
	productHeaderId,
	ProductViewTab,
} from '~/Slices/schema';
import { fetchCurrentCustomerSuppliers } from '~/Slices/SuppliersSlice';

export interface IMetaProductPageState {
	loadingStatus: LoadingStatus;
	savingStatus: LoadingStatus;
	metaProduct: IMetaProduct;
	blueprintId: number;
	metaProductGrouping: Hub.MetaProductGrouping | null;
	selectedProductsPricingStatus: LoadingStatus;
	showExpandedDescriptions: boolean;
	isDssiSiteType: boolean;
	isSyscoSiteType: boolean;
	showCategories: boolean;
	showGLs: boolean;
	loadGLStatus: LoadingStatus;
	isPageDirty: boolean;
	productSearchResults: IProduct[];
	productViewTabSelected: string;
	gridViewDisplayType: GridViewDisplayType;
	searchStatus: LoadingStatus;
	productNumberLookupStatus: LoadingStatus;
	searchTotalResults: number;
	searchPricingStatus: LoadingStatus;
	searchParams: FetchSearchResultsParams;
	selectedDcsForSearch: IDistributionCenter[];
	showOnlyAdditionalCoverage: boolean;
	recommendationsShowOnlyAdditionalCoverage: boolean;
	includeOpenSpecCoverage: boolean;
	searchFilterModalOpen: boolean;
	searchFilters: ISearchFiltersAvailable;
	productLineStatus: LoadingStatus;
	productLinePricingStatus: LoadingStatus;
	productUsageStatus: LoadingStatus;
	recommendedProducts: IProductWithNetUnits[];
	recommendationsPricingStatus: LoadingStatus;
	selectionRuleSets: SelectionRuleSet[];
	maintainedBys: number[];
	specIndicatorEnabled: boolean;
	editSpecIndicator: boolean;
	specIndicators: number[];
	distributionCenters: IDistributionCenter[];
	selectedProductKey: IProduct;
	enabledLocations: ILocation[];
	selectedLocationId: number;
	showProductAvailability: boolean;
	productAvailabilityStatus: LoadingStatus;
	productAvailabilityResultStatus: AvailabilityResultState;
	showSearchProductAvailability: boolean;
	searchProductAvailabilityStatus: LoadingStatus;
	searchProductAvailabilityResultStatus: AvailabilityResultState;
	showGlAssignmentModal: boolean;
	showPhaseOutAlternatesModal: boolean;
	phaseOutAlternatesProductHeader: IProductHeader;
	phaseOutAlternatesSelectedProducts: string[];
	showAddBySupcModal: boolean;
	showMetaProductIsSelectByUmWarningModal: boolean;
	rebateApplied: boolean;
	customCategories: ICustomCategory[];
	existingNetUnits: ProductNetUnits[];
	metaProductPartOfGroupingLine: MetaProductGroupingSummaryLine;
	supplierCode: string;
	usFoodsProductAttributes: USFoodsProductAttributes[];
	usFoodsLoadingStatus: LoadingStatus;
	showProductAttributes: boolean;
	showWeightInOunces: boolean;
	checkedSageSelectProducts: string[];
	isShowingAddAndOptimizeWarningModal: boolean;
	dontShowAddAndOptimizeWarningModalSetting: boolean;
	dontShowAddAndOptimizeWarningModalSettingCheckbox: boolean;
}

export interface ISearchFiltersAvailable {
	categoryLevel1Facet: Hub.SearchFacet[];
	categoryLevel2Facet: Hub.SearchFacet[];
	categoryLevel3Facet: Hub.SearchFacet[];
	categoryLevel4Facet: Hub.SearchFacet[];
	brandFacet: Hub.SearchFacet[];
	packSizeFacet: Hub.SearchFacet[];
}

export type IProductSpecIndicatorDict = Record<string, number | null>;

export interface ISaveMetaProductToastMessage {
	isAddAndOptimize: boolean;
	routedFromOgmAi: boolean;
	toastMessage: IToastMessage;
}

export interface IAddToSaveOptimizeData {
	headers: IProductHeader[];
	isSelectByUm: boolean;
	isAddAndOptimize: boolean;
	toastMessage: IToastMessage;
	RecalculateOptimizationArgs: Hub.RecalculateOptimizationArgs;
}

export enum SearchFilterType {
	CategoryLevel1,
	CategoryLevel2,
	CategoryLevel3,
	CategoryLevel4,
	Brand,
	PackSize,
}

export interface SearchFilterChangedArgs {
	type: SearchFilterType;
	values: string[] | null;
}

export interface ILoadMetaProductParameters {
	metaProductId: number;
	blueprintId: number;
	searchTerm: string;
	fromOgmAi: boolean;
}

export interface ILocation {
	enabled: boolean;
	id: number;
	customerId: number;
	name: string;
	addressLine1: string;
	addressLine2: string;
	city: string;
	state: string;
	zip: string;
	locationCode: string;
	externalCode: string;
	distributionCenterExternalCode: string;
	distributionCenterName: string;
}

export const initialState: IMetaProductPageState = {
	loadingStatus: LoadingStatus.idle,
	savingStatus: LoadingStatus.idle,
	metaProduct: null,
	blueprintId: 0,
	metaProductGrouping: null,
	showExpandedDescriptions: true,
	isDssiSiteType: User.isDssiSiteType(),
	isSyscoSiteType: User.isSyscoSiteType(),
	showCategories: false,
	showGLs: false,
	loadGLStatus: LoadingStatus.idle,
	isPageDirty: false,
	productSearchResults: [],
	searchStatus: LoadingStatus.idle,
	productNumberLookupStatus: LoadingStatus.idle,
	selectedProductsPricingStatus: LoadingStatus.idle,
	searchTotalResults: 0,
	searchPricingStatus: LoadingStatus.idle,
	showOnlyAdditionalCoverage:
		LocalStorage.getShowOnlyAdditionalCoverage() ?? false,
	recommendationsShowOnlyAdditionalCoverage: false,
	includeOpenSpecCoverage: false,
	searchParams: {
		searchTerm: '',
		pageIndex: 0,
		pageLimit: 20,
		corpSupplierCodesFilter: [],
		distributionCentersFilter: [],
		categoryLevel1Filter: undefined,
		categoryLevel2Filter: undefined,
		categoryLevel3Filter: undefined,
		categoryLevel4Filter: undefined,
		brandFilter: undefined,
		packSizeFilter: undefined,
		allowedStockTypes: undefined,
		includeOnlyEligibleItems:
			LocalStorage.getShowOnlyAdditionalCoverage() ?? false,
	},
	selectedDcsForSearch: [],
	searchFilters: {
		categoryLevel1Facet: [],
		categoryLevel2Facet: [],
		categoryLevel3Facet: [],
		categoryLevel4Facet: [],
		brandFacet: [],
		packSizeFacet: [],
	},
	productLineStatus: LoadingStatus.idle,
	productLinePricingStatus: LoadingStatus.idle,
	productUsageStatus: LoadingStatus.idle,
	recommendedProducts: [],
	recommendationsPricingStatus: LoadingStatus.idle,
	searchFilterModalOpen: false,
	selectionRuleSets: [],
	maintainedBys: AvailableMaintainedByTypes,
	specIndicatorEnabled: false,
	specIndicators: AvailableSpecIndicatorTypes,
	editSpecIndicator: false,
	distributionCenters: [],
	selectedProductKey: {
		corpSupplierCode: '',
		supplierProductNumber: '',
		unitMeasureCode: null,
	},
	enabledLocations: [],
	selectedLocationId: -1,
	showProductAvailability: false,
	productAvailabilityStatus: LoadingStatus.idle,
	productAvailabilityResultStatus: AvailabilityResultState.None,
	showSearchProductAvailability: false,
	productViewTabSelected: ProductViewTab.selection,
	gridViewDisplayType: GridViewDisplayType.coverage,
	searchProductAvailabilityStatus: LoadingStatus.idle,
	searchProductAvailabilityResultStatus: AvailabilityResultState.None,
	showGlAssignmentModal: false,
	showPhaseOutAlternatesModal: false,
	phaseOutAlternatesProductHeader: undefined,
	phaseOutAlternatesSelectedProducts: [],
	showAddBySupcModal: false,
	showMetaProductIsSelectByUmWarningModal: false,
	rebateApplied: LocalStorage.getShowEstNetPrice() ?? false,
	customCategories: [],
	existingNetUnits: [],
	metaProductPartOfGroupingLine: null,
	supplierCode: '',
	usFoodsProductAttributes: [],
	usFoodsLoadingStatus: LoadingStatus.idle,
	showProductAttributes: LocalStorage.getShowProductAttributes() ?? false,
	showWeightInOunces: LocalStorage.getShowWeightInOunces() ?? false,
	checkedSageSelectProducts: [],
	isShowingAddAndOptimizeWarningModal: false,
	dontShowAddAndOptimizeWarningModalSetting:
		LocalStorage.getDontShowAddAndOptimizeWarningModal() ?? false,
	dontShowAddAndOptimizeWarningModalSettingCheckbox: false,
};

export const loadMetaProduct = createAsyncThunk<
	IMetaProduct,
	ILoadMetaProductParameters,
	{ state: IAppState; rejectValue: string[] | Response }
>(
	'metaProductPage/loadMetaProduct',
	async (
		params: ILoadMetaProductParameters,
		{ getState, dispatch, rejectWithValue }
	) => {
		const { blueprintId, metaProductId } = params;
		const productParams = [];
		const productNumbers = [];
		let metaproduct: IMetaProduct;
		if (metaProductId != null) {
			const resultAction = await dispatch(fetchMetaProduct(params));
			await dispatch(loadGrouping(metaProductId));
			await dispatch(getGroupingByMasterItem(metaProductId));
			await dispatch(SuggestionsSlice.getOptimizationDetails(metaProductId));
			if (fetchMetaProduct.fulfilled.match(resultAction)) {
				const products = resultAction.payload.entities.productHeaders;
				for (const key in products) {
					if (Object.prototype.hasOwnProperty.call(products, key)) {
						productParams.push({
							corpSupplierCode: products[key].corpSupplierCode,
							supplierProductNumber: products[key].supplierProductNumber,
						});
						productNumbers.push(products[key].supplierProductNumber);
					}
				}
				metaproduct =
					resultAction.payload.entities.metaProducts[params.metaProductId];
			} else {
				rejectWithValue(resultAction.payload);
			}
		} else {
			const selectionRuleSets = Customer.customer().selectionRuleSets;
			const defaultMaintainedByResults = await unwrapFetch2(
				Hub.BlueprintControllerV3.GetBlueprintDefaultMetaProductMaintainedBy(
					blueprintId
				)
			);

			metaproduct = {
				id: null,
				choices: null,
				name: '',
				lastSearchTerm: '',
				locationsWithCoverageCount: 0,
				distributionCentersWithCoverageCount: 0,
				products: [],
				productHeaders: [],
				distributionCenterPriorityOverrides: [],
				maintainedBy:
					defaultMaintainedByResults?.maintainedBy ==
					Hub.MaintainedBy.OGMAIManual
						? IMaintainedByType.OGMAIManual
						: IMaintainedByType.OGM3,
				statusId: IMetaProductStatus.Active,
				selectionRuleSetId:
					selectionRuleSets.findIndex((x) => x.isDefault) > -1
						? selectionRuleSets.find((x) => x.isDefault).id
						: selectionRuleSets?.length
						? selectionRuleSets[0].id
						: -1,
				pendingRebuild: false,
				specIndicatorId: null,
				customCategoryId: null,
				productSpecIndicatorIds: {},
				isSelectByUm: false,
			};
		}

		const state: IAppState = getState();

		Promise.all([
			dispatch(
				metaProductPageSlice.actions.setSelectionRuleSets(
					Customer.customer().selectionRuleSets
				)
			),
			dispatch(
				metaProductPageSlice.actions.setSpecIndicatorEnable(
					Customer.specIndicatorEnabled()
				)
			),
			dispatch(fetchSelectedHeaderPrices(productNumbers)),
			dispatch(loadBlueprint(blueprintId)),
			dispatch(loadSupplier(blueprintId)),
			dispatch(loadDistributionCenters(blueprintId)).then(() => {
				const newState = getState();
				const distributionCenterDssiCodes =
					newState.metaProductPage.page.distributionCenters.map(
						(dc) => dc.dssiCode
					);

				if (metaproduct.products.length > 0) {
					const headers = selectProductHeaders(
						newState,
						metaproduct.products.map(
							(p) => `${p.corpSupplierCode}|${p.supplierProductNumber}`
						)
					);

					if (state.metaProductPage.page.showGLs) {
						const loadGlParams = headers.map((p) => {
							return {
								corpSupplierCode: p.corpSupplierCode,
								supplierProductNumber: p.supplierProductNumber,
								distributionCenterCodes: distributionCenterDssiCodes,
							};
						});
						dispatch(loadGLs(loadGlParams));
					}

					dispatch(
						getProductUsageHistory({
							productsGroupedBySupplier: [
								{
									corpSupplierCode: headers?.[0].corpSupplierCode,
									productNumbers: headers.map((x) => x.supplierProductNumber),
									distributionCenterDssiCodes,
								},
							],
							blueprintId,
						})
					);
				}
			}),
		]).finally(() => {
			const state = getState();
			if (params.searchTerm) {
				const defaultSearchParams = state.metaProductPage.page.searchParams;
				dispatch(
					search({ ...defaultSearchParams, searchTerm: params.searchTerm })
				);
			}

			const specProduct = productParams[0];
			if (specProduct != null) {
				const recommendationsParams = {
					blueprintId,
					productIdentifiers: [
						{
							corpSupplierCode: specProduct.corpSupplierCode,
							supplierProductNumber: specProduct.supplierProductNumber,
						},
					],
				};
				dispatch(loadRecommendations(recommendationsParams));
			}

			if (state.metaProductPage.page.supplierCode == 'USFO') {
				dispatch(
					fetchUSFoodsProductAttributes({
						supplierProductNumbers: productNumbers,
						distributionCenterDssiCodes:
							state.metaProductPage.page.distributionCenters.map(
								(dc) => dc.dssiCode
							),
					})
				);
			}
		});

		if (Customer.customCategoriesEnabled()) {
			dispatch(loadCustomCategories({}));
		}

		getMetaProductChoicesArgs(
			getState,
			null,
			null,
			null,
			null,
			metaproduct.selectionRuleSetId,
			metaproduct
		).then((choices) =>
			dispatch(metaProductPageSlice.actions.setChoices(choices))
		);

		return metaproduct;
	}
);

export const getGroupingByMasterItem = createAsyncThunk<
	MetaProductGroupingSummaryLine,
	number,
	{ state: IAppState; rejectValue: string[] | Response }
>(
	'metaProductPage/getGroupingByMasterItem',
	async (masterItemId: number, { getState, rejectWithValue }) => {
		try {
			const requestArgs = getGroupingSearchArgs(masterItemId, getState);
			const result = await unwrapFetch2(
				MetaProductController.SearchMetaProductGroupings(requestArgs)
			);
			if (!result.results.length) {
				return null;
			}
			const stagedResult = result.results.find(
				(x) => x.status === MetaProductGroupingStatus.Staged
			);
			const machineResult = result.results.find(
				(x) => x.source === MetaProductGroupingSource.Machine
			);
			return stagedResult ?? machineResult ?? null;
		} catch (err) {
			if (err instanceof ApiRequestError) {
				return rejectWithValue(err.errors);
			}
			if (err instanceof ApiServerError) {
				return rejectWithValue(err.response);
			}
			throw err;
		}
	}
);

const getGroupingSearchArgs = (masterItemId: number | null, getState) => {
	const state = getState().OGMAISuggestions;
	const customerId = LocalStorage.getCustomerId();
	const orderGuideIds = state.selectedOrderGuides?.map((x) => x.id);
	const sourceIds = [
		MetaProductGroupingSource.Machine,
		MetaProductGroupingSource.Ai,
	];
	const date = new Date();
	date.setDate(
		date.getDate() -
			(state.selectedGroupedDateFilter
				? state.selectedGroupedDateFilterDateFilter
				: 0)
	);
	const notGroupedAfter = state.selectedGroupedDateFilter
		? date.toISOString()
		: null;
	const pageIndex = state.groupingsPage * state.pageSize;
	const requestArgs: SearchMetaProductGroupingArgsV6 = {
		metaProductIds: masterItemId ? [masterItemId] : null,
		statusIds: null,
		sourceIds: sourceIds,
		startDate: null,
		endDate: null,
		customerId: customerId,
		blueprintIds: orderGuideIds,
		distributionCenterId: null,
		searchTerm: state.searchTerm,
		pageSize: state.pageSize,
		pageIndex: pageIndex,
		notGroupedAfter: notGroupedAfter,
	};

	return requestArgs;
};

export const loadBlueprint = createAsyncThunk<
	void,
	number,
	{ state: IAppState; rejectValue: string[] | Response }
>(
	'metaProductPage/loadBlueprint',
	async (blueprintId: number, { dispatch }) => {
		const result = await dispatch(fetchBlueprint(blueprintId));
		const supplierId = (result.payload as IBlueprint).supplierId;
		dispatch(loadSupplier(supplierId));
		if ((result.payload as IBlueprint).supplierProvidesRTA) {
			dispatch(fetchActiveLocations(blueprintId));
		}
	}
);

export const loadGrouping = createAsyncThunk<
	Hub.MetaProductGrouping | null,
	number,
	{ state: IAppState; rejectValue: string[] | Response }
>('metaProductPage/loadGrouping', async (metaProductId: number) => {
	const results = await unwrapFetch2(
		Hub.MetaProductController.GetMetaProductGroups({
			metaProductIds: [metaProductId],
			groupingIds: [],
		})
	);

	if (results.groupings.length > 0) {
		return results.groupings[0];
	}

	return null;
});

export const loadSupplier = createAsyncThunk<
	void,
	number,
	{ state: IAppState; rejectValue: string[] | Response }
>('metaProductPage/loadSupplier', async (supplierId: number, { dispatch }) => {
	const result = await dispatch(fetchCurrentCustomerSuppliers());
	const customerSuppliersByCode = result.payload as Dictionary<ISupplier>;
	let supplierCode: string;
	for (const key in customerSuppliersByCode) {
		if (Object.prototype.hasOwnProperty.call(customerSuppliersByCode, key)) {
			if (customerSuppliersByCode[key].id === supplierId) {
				supplierCode = customerSuppliersByCode[key].dssiCode;
			}
		}
	}
	dispatch(metaProductPageSlice.actions.searchSupplierUpdate([supplierCode]));
	dispatch(metaProductPageSlice.actions.setSupplierCode(supplierCode));
});

export const fetchSearchHeaderPrices = createAsyncThunk<
	void,
	string[],
	{ state: IAppState; rejectValue: string[] }
>(
	'metaProductPage/fetchSearchHeaderPrices',
	async (params: string[], { dispatch, getState }) => {
		const blueprintId = getState().metaProductPage.page.blueprintId;

		await dispatch(
			fetchHeaderLevelBlueprintPrices({ blueprintId, productNumbers: params })
		);

		const state = getState();

		if (
			state.metaProductPage.page.searchProductAvailabilityStatus ===
			LoadingStatus.success
		) {
			dispatch(loadSearchAvailability());
		}
	}
);

export const fetchSelectedHeaderPrices = createAsyncThunk<
	void,
	string[],
	{ state: IAppState; rejectValue: string[] }
>(
	'metaProductPage/fetchSelectedHeaderPrices',
	async (params: string[], { getState, dispatch }) => {
		const blueprintId = getState().metaProductPage.page.blueprintId;

		await dispatch(
			fetchHeaderLevelBlueprintPrices({ blueprintId, productNumbers: params })
		);
	}
);

export const fetchRecommendedHeaderPrices = createAsyncThunk<
	void,
	string[],
	{ state: IAppState; rejectValue: string[] }
>(
	'metaProductPage/fetchRecommendedHeaderPrices',
	async (params: string[], { getState, dispatch }) => {
		const blueprintId = getState().metaProductPage.page.blueprintId;

		await dispatch(
			fetchHeaderLevelBlueprintPrices({ blueprintId, productNumbers: params })
		);
	}
);

export const fetchUSFoodsProductAttributes = createAsyncThunk<
	USFoodsProductAttributes[],
	GetUSFoodsProductAttributesArgs,
	{ state: IAppState; rejectValue: string[] | Response }
>(
	'metaProductPage/fetchUSFoodsProductAttributes',
	async (args: GetUSFoodsProductAttributesArgs) => {
		try {
			const results = await unwrapFetch2(
				Hub.GetUSFoodsProductAttributesV1.GetUsFoodsProductAttributes(args)
			);
			return results as USFoodsProductAttributes[];
		} catch (err) {
			return [];
		}
	}
);

export const loadRecommendations = createAsyncThunk<
	IProductWithNetUnits[],
	FetchRecommendationsParams,
	{ state: IAppState; rejectValue: string[] | Response }
>(
	'metaProductPage/loadRecommendations',
	async (
		params: FetchRecommendationsParams,
		{ rejectWithValue, dispatch, getState }
	) => {
		const state = getState();
		const { blueprintId } = state.metaProductPage.page;

		const result = await dispatch(fetchRecommendations(params));

		if (fetchRecommendations.fulfilled.match(result)) {
			if (result.payload.length > 0) {
				const productNumbers = result.payload.map(
					(p) => p.supplierProductNumber
				);
				const distributionCenterDssiCodes =
					getState().metaProductPage.page.distributionCenters.map(
						(dc) => dc.dssiCode
					);
				dispatch(fetchRecommendedHeaderPrices(productNumbers));
				dispatch(
					getProductUsageHistory({
						productsGroupedBySupplier: [
							{
								corpSupplierCode: result.payload[0].corpSupplierCode,
								productNumbers: productNumbers,
								distributionCenterDssiCodes,
							},
						],
						blueprintId,
					})
				);
				if (state.metaProductPage.page.supplierCode == 'USFO') {
					dispatch(
						fetchUSFoodsProductAttributes({
							supplierProductNumbers: productNumbers,
							distributionCenterDssiCodes: distributionCenterDssiCodes,
						})
					);
				}
			}
			return result.payload.map((x) => {
				return {
					corpSupplierCode: x.corpSupplierCode,
					supplierProductNumber: x.supplierProductNumber,
					unitMeasureCode: null,
					productNetUnits: x.productNetUnits,
				};
			});
		}

		return rejectWithValue(result.payload);
	}
);

export const search = createAsyncThunk<
	FetchSearchResults,
	FetchSearchResultsParams,
	{ state: IAppState; rejectValue: string[] | Response }
>(
	'metaProductPage/search',
	async (
		searchParams: FetchSearchResultsParams,
		{ dispatch, rejectWithValue, getState }
	) => {
		dispatch(metaProductPageSlice.actions.searchParamsChange(searchParams));

		const state = getState();

		const dcSearchParams = { ...searchParams };

		if (dcSearchParams.includeOnlyEligibleItems) {
			const dcs = getUnCoveredDcs(state).filter((dc) =>
				dcSearchParams.distributionCentersFilter.includes(dc)
			);
			dcSearchParams.distributionCentersFilter = dcs;
		}

		if (dcSearchParams.distributionCentersFilter.length === 0) {
			return {
				currentPage: [],
				productHeaders: [],
				totalPages: 0,
				totalResults: 0,
				supplierFacet: null,
				categoryLevel1Facet: null,
				categoryLevel2Facet: null,
				categoryLevel3Facet: null,
				categoryLevel4Facet: null,
				brandFacet: null,
				packSizeFacet: null,
			};
		}

		const result = await dispatch(fetchSearchResults(dcSearchParams));

		if (fetchSearchResults.fulfilled.match(result)) {
			const searchResults = (result.payload as any)
				.productHeaders as IProductHeader[];
			const resultSet = result.payload;
			const { blueprintId } = state.metaProductPage.page;

			if (!searchResults || searchResults.length <= 0) {
				return resultSet;
			}

			if (searchParams.packSizeFilter) {
				const validPackSizeFilters = searchParams.packSizeFilter.filter((x) =>
					resultSet.packSizeFacet?.find((y) => y.displayString === x)
				);
				if (validPackSizeFilters.length < searchParams.packSizeFilter.length) {
					dispatch(
						search({
							...searchParams,
							packSizeFilter: validPackSizeFilters,
						})
					);
					return rejectWithValue(['Retrying with valid pack sizes']);
				}
			}

			const productNumbers = searchResults.map((p) => p.supplierProductNumber);
			dispatch(fetchSearchHeaderPrices(productNumbers));
			dispatch(
				getProductUsageHistory({
					productsGroupedBySupplier: [
						{
							productNumbers,
							corpSupplierCode: searchResults[0].corpSupplierCode,
							distributionCenterDssiCodes:
								dcSearchParams.distributionCentersFilter,
						},
					],
					blueprintId,
				})
			);

			if (state.metaProductPage.page.supplierCode == 'USFO') {
				dispatch(
					fetchUSFoodsProductAttributes({
						supplierProductNumbers: productNumbers,
						distributionCenterDssiCodes:
							state.metaProductPage.page.distributionCenters.map(
								(dc) => dc.dssiCode
							),
					})
				);
			}

			return resultSet;
		} else if (
			fetchSearchResults.rejected.match(result) &&
			result.error?.name === 'AbortError'
		) {
			return rejectWithValue(['Aborted']);
		}
		return rejectWithValue(result.payload);
	}
);

export const changeSearchFilter = createAsyncThunk<
	void,
	SearchFilterChangedArgs,
	{ state: IAppState; rejectValue: string[] }
>(
	'metaProductPage/changeSearchFilter',
	async (
		filterChangedArgs: SearchFilterChangedArgs,
		{ dispatch, getState }
	) => {
		const state = getState();
		const searchParams = {
			...state.metaProductPage.page.searchParams,
			pageIndex: 0,
		};
		switch (filterChangedArgs.type) {
			case SearchFilterType.CategoryLevel1:
				searchParams.categoryLevel1Filter = filterChangedArgs.values?.[0];
				searchParams.categoryLevel2Filter = undefined;
				searchParams.categoryLevel3Filter = undefined;
				searchParams.categoryLevel4Filter = undefined;
				break;
			case SearchFilterType.CategoryLevel2:
				searchParams.categoryLevel2Filter = filterChangedArgs.values?.[0];
				searchParams.categoryLevel3Filter = undefined;
				searchParams.categoryLevel4Filter = undefined;
				break;
			case SearchFilterType.CategoryLevel3:
				searchParams.categoryLevel3Filter = filterChangedArgs.values?.[0];
				searchParams.categoryLevel4Filter = undefined;
				break;
			case SearchFilterType.CategoryLevel4:
				searchParams.categoryLevel4Filter = filterChangedArgs.values?.[0];
				break;
			case SearchFilterType.Brand:
				searchParams.brandFilter = filterChangedArgs.values?.[0];
				break;
			case SearchFilterType.PackSize:
				searchParams.packSizeFilter = filterChangedArgs.values;
				break;
		}
		await dispatch(search(searchParams));
	}
);

export const changeSearchDcFilter = createAsyncThunk<
	void,
	IDistributionCenter[],
	{ state: IAppState }
>(
	'metaProductPage/changeSearchDcFilter',
	async (dcFilter: IDistributionCenter[], { dispatch, getState }) => {
		const state = getState();
		const searchParams = {
			...state.metaProductPage.page.searchParams,
			pageIndex: 0,
		};
		const {
			metaProductPage: {
				page: { distributionCenters },
			},
		} = state;

		const dcs =
			dcFilter.length > 0
				? dcFilter.map((dc) => dc.dssiCode)
				: distributionCenters.map((dc) => dc.dssiCode);

		dispatch(metaProductPageSlice.actions.dcFilterChange(dcFilter));

		searchParams.distributionCentersFilter = dcs;

		await dispatch(search(searchParams));
	}
);

export const toggleShowAdditionalCoverage = createAsyncThunk<
	void,
	void,
	{ state: IAppState; rejectValue: string[] }
>(
	'metaProductPage/toggleShowAdditionalCoverage',
	async (_, { dispatch, getState }) => {
		const state = getState();
		const { showOnlyAdditionalCoverage } = state.metaProductPage.page;

		const newShowOnlyAdditionalCoverage = !showOnlyAdditionalCoverage;
		LocalStorage.setShowOnlyAdditionalCoverage(newShowOnlyAdditionalCoverage);

		dispatch(
			metaProductPageSlice.actions.onlyAdditionalCoverageChange(
				newShowOnlyAdditionalCoverage
			)
		);

		const searchParams = {
			...state.metaProductPage.page.searchParams,
			includeOnlyEligibleItems: newShowOnlyAdditionalCoverage,
			pageIndex: 0,
		};
		await dispatch(search(searchParams));
	}
);

export const toggleRecommendationsShowAdditionalCoverage = createAsyncThunk<
	void,
	boolean,
	{ state: IAppState; rejectValue: string[] }
>(
	'metaProductPage/toggleRecommendationsShowAdditionalCoverage',
	async (isOpenSpec, { dispatch, getState }) => {
		const state = getState();
		const {
			recommendationsShowOnlyAdditionalCoverage,
			includeOpenSpecCoverage,
		} = state.metaProductPage.page;

		let newShowOnlyAdditionalCoverage =
			recommendationsShowOnlyAdditionalCoverage;
		let newIncludeOpenSpecCoverage = includeOpenSpecCoverage;
		if (isOpenSpec) {
			newIncludeOpenSpecCoverage = !includeOpenSpecCoverage;
		} else {
			newShowOnlyAdditionalCoverage =
				!recommendationsShowOnlyAdditionalCoverage;
		}

		dispatch(
			metaProductPageSlice.actions.recommendationsCoverageChange({
				newShowOnlyAdditionalCoverage,
				newIncludeOpenSpecCoverage,
			})
		);
	}
);

export const changePage = createAsyncThunk<
	void,
	number,
	{ state: IAppState; rejectValue: string[] }
>(
	'metaProductPage/changePage',
	async (pageIndex: number, { dispatch, getState }) => {
		const state = getState();
		const searchParams = {
			...state.metaProductPage.page.searchParams,
			pageIndex,
		};
		await dispatch(search(searchParams));
	}
);

export const toggleShowCategoriesAndGLs = createAsyncThunk<
	void,
	void,
	{ state: IAppState; rejectValue: string[] }
>(
	'metaProductPage/toggleShowCategoriesAndGLs',
	async (_, { dispatch, getState }) => {
		dispatch(metaProductPageSlice.actions.toggleCategoriesAndGLs());
		const state = getState();
		if (state.metaProductPage.page.showGLs) {
			const productHeaders = selectProductHeaders(
				state,
				state.metaProductPage.page.metaProduct.products.map(
					(p) => `${p.corpSupplierCode}|${p.supplierProductNumber}`
				)
			);
			const loadGlParams = productHeaders.map((p) => {
				return {
					corpSupplierCode: p.corpSupplierCode,
					supplierProductNumber: p.supplierProductNumber,
					distributionCenterCodes:
						state.metaProductPage.page.distributionCenters.map(
							(dc) => dc.dssiCode
						),
				};
			});
			await dispatch(loadGLs(loadGlParams));
		}
	}
);

export const showPhaseOutAlternatesModal = createAsyncThunk<
	void,
	IProductHeader,
	{ state: IAppState; rejectValue: string[] }
>(
	'metaProductPage/toggleShowCategoriesAndGLs',
	async (productHeader: IProductHeader, { dispatch, getState }) => {
		const state = getState();
		const blueprintId = state.metaProductPage.page.blueprintId;
		dispatch(
			metaProductPageSlice.actions.showPhaseOutAlternatesModal(productHeader)
		);
		await dispatch(
			fetchProductLines({ blueprintId, productHeaders: [productHeader] })
		);
	}
);

export const loadCustomCategories = createAsyncThunk<
	ICustomCategory[],
	{},
	{ state: IAppState; rejectValue: string[] | Response }
>('metaProductPage/loadCustomCategories', async (_, { rejectWithValue }) => {
	try {
		return await unwrapFetch2(
			fetch2<ICustomCategory[]>({
				uri: endpoint.customCategories(),
				method: 'GET',
			})
		);
	} catch (err) {
		if (err instanceof ApiRequestError) {
			return rejectWithValue(err.errors);
		}
		if (err instanceof ApiServerError) {
			return rejectWithValue(err.response);
		}
		throw err;
	}
});

export const loadGLs = createAsyncThunk<
	void,
	IFetchGLGroupsForProductParms[],
	{ state: IAppState; rejectValue: string[] }
>(
	'metaProductPage/loadGLs',
	async (productHeaders: IFetchGLGroupsForProductParms[], { dispatch }) => {
		await dispatch(fetchGLGroups(productHeaders));
	}
);

export const onSaveMetaProduct = createAsyncThunk<
	void,
	ISaveMetaProductToastMessage,
	{ state: IAppState; rejectValue: string[] | Response }
>(
	'metaProductPage/onSaveMetaProduct',
	async (
		saveParams: ISaveMetaProductToastMessage,
		{ rejectWithValue, dispatch, getState }
	) => {
		try {
			const state = getState();
			const params = {
				metaProduct: state.metaProductPage.page.metaProduct,
				blueprintId: state.metaProductPage.page.blueprintId,
				applyRelatedGrouping:
					state.metaProductPage.page.metaProduct.statusId ===
						IMetaProductStatus.Draft &&
					state.metaProductPage.page.metaProductGrouping != null,
				groupingState: state.metaProductPage.page.metaProductGrouping?.statusId,
				groupingSource: state.metaProductPage.page.metaProductGrouping?.source,
				recalculateOptimization: saveParams.isAddAndOptimize ? false : true,
			};

			await dispatch(saveMetaProduct(params));
			dispatch(metaProductPageSlice.actions.setIsPageDirty(false));
			toastr.success(saveParams.toastMessage);

			if (!saveParams.isAddAndOptimize) {
				if (ManageMetaProducts.onExceptionWorkflow()) {
					dispatch(push(`/alerts`));
				} else if (
					state.rearrange.groupings.remainingGroupingIdsToReview.length > 1
				) {
					dispatch(goBack());
				} else if (saveParams.routedFromOgmAi) {
					dispatch(push(`/ogmai/${OGMAIViewTab.suggestedOptimization}`));
				} else if (
					params.applyRelatedGrouping &&
					(params.groupingSource === MetaProductGroupingSource.Machine ||
						params.groupingSource === MetaProductGroupingSource.Ai)
				) {
					dispatch(push(`/ogmai/${OGMAIViewTab.grouping}`));
				} else {
					dispatch(
						push(
							route.masterItemListing(
								state.metaProductPage.page.blueprintId.toString()
							)
						)
					);
				}
			}
		} catch (err) {
			if (err instanceof ApiRequestError) {
				return rejectWithValue(err.errors);
			}
			if (err instanceof ApiServerError) {
				return rejectWithValue(err.response);
			}
			throw err;
		}
	}
);

export const onDeleteMetaProduct = createAsyncThunk<
	void,
	void,
	{ state: IAppState; rejectValue: string[] | Response }
>(
	'metaProductPage/onDeleteMetaProduct',
	async (_, { rejectWithValue, dispatch, getState }) => {
		try {
			const state = getState();
			const params = {
				metaProductId: state.metaProductPage.page.metaProduct.id,
				blueprintId: state.metaProductPage.page.blueprintId,
			};

			await dispatch(deleteMetaProduct(params));
			toastr.success({
				title: 'Master Item Deleted',
				body: 'The master item was successfully deleted.',
			});

			if (ManageMetaProducts.onExceptionWorkflow()) {
				dispatch(push(`/alerts`));
			} else {
				dispatch(
					push(
						route.masterItemListing(
							state.metaProductPage.page.blueprintId.toString()
						)
					)
				);
			}
		} catch (err) {
			toastr.error({
				title: 'Error Deleting Master Item',
				body: 'There was a problem deleting the master item. Please refresh the page and try again.',
			});
			if (err instanceof ApiRequestError) {
				return rejectWithValue(err.errors);
			}
			if (err instanceof ApiServerError) {
				return rejectWithValue(err.response);
			}
			throw err;
		}
	}
);

export const loadDistributionCenters = createAsyncThunk<
	IDistributionCenter[],
	number,
	{ rejectValue: string[] | Response }
>(
	'metaProductPage/loadDistributionCenters',
	async (blueprintId: number, { dispatch, rejectWithValue }) => {
		try {
			const result = await dispatch(
				fetchDistributionCentersForBlueprint(blueprintId)
			);
			return result.payload as IDistributionCenter[];
		} catch (err) {
			if (err instanceof ApiRequestError) {
				return rejectWithValue(err.errors);
			}
			if (err instanceof ApiServerError) {
				return rejectWithValue(err.response);
			}
			throw err;
		}
	}
);
export const addProductToMetaProduct = createAsyncThunk<
	void,
	IProduct,
	{ state: IAppState; rejectValue: string[] | Response }
>(
	'metaProductPage/addProductToMetaProduct',
	async (newProduct: IProduct, { dispatch, getState }) => {
		const state = getState();
		const productHeader =
			state.productHeaders.entities[
				`${newProduct.corpSupplierCode}|${newProduct.supplierProductNumber}`
			];
		const { products, isSelectByUm } = state.metaProductPage.page.metaProduct;
		const { blueprintId, searchParams, showGLs } = state.metaProductPage.page;

		if (isSelectByUm) {
			const productsToAdd: IProduct[] = [];
			productHeader.unitMeasureCodes.forEach((um) => {
				if (
					products.findIndex(
						(x) =>
							x.corpSupplierCode === productHeader.corpSupplierCode &&
							x.supplierProductNumber === productHeader.supplierProductNumber &&
							x.unitMeasureCode === um
					) < 0
				) {
					productsToAdd.push({
						corpSupplierCode: productHeader.corpSupplierCode,
						supplierProductNumber: productHeader.supplierProductNumber,
						unitMeasureCode: um,
					});
				}
			});
			if (productsToAdd.length > 0) {
				getMetaProductChoicesArgs(
					getState,
					null,
					null,
					productsToAdd,
					null
				).then((choices) => {
					dispatch(
						metaProductPageSlice.actions.addProductsToMetaProduct({
							productsToAdd,
							choices,
						})
					);
					dispatch(search(searchParams));
				});
				if (state.metaProductPage.page.metaProduct.name === '') {
					const metaProductName =
						selectProductHeader(
							state,
							state.metaProductPage.page.metaProduct.products.map(
								(p) => `${p.corpSupplierCode}|${p.supplierProductNumber}`
							)[0]
						)?.description ||
						productHeader.description ||
						'';
					dispatch(
						metaProductPageSlice.actions.metaProductNameChange(metaProductName)
					);
				}

				if (showGLs && productHeader.glGroup == null) {
					const loadGlParams = {
						corpSupplierCode: productHeader.corpSupplierCode,
						supplierProductNumber: productHeader.supplierProductNumber,
						distributionCenterCodes:
							state.metaProductPage.page.distributionCenters.map(
								(dc) => dc.dssiCode
							),
					};
					dispatch(loadGLs([loadGlParams]));
				}

				if (products.length === 0) {
					const recommendationsParams = {
						blueprintId,
						productIdentifiers: [
							{
								corpSupplierCode: productHeader.corpSupplierCode,
								supplierProductNumber: productHeader.supplierProductNumber,
							},
						],
					};
					dispatch(loadRecommendations(recommendationsParams));
				}

				dispatch(
					getProductUsageHistory({
						productsGroupedBySupplier: [
							{
								productNumbers: [productHeader.supplierProductNumber],
								corpSupplierCode: productHeader.corpSupplierCode,
								distributionCenterDssiCodes:
									state.metaProductPage.page.distributionCenters.map(
										(dc) => dc.dssiCode
									),
							},
						],
						blueprintId,
					})
				);
			}
		} else {
			if (
				products.findIndex(
					(x) =>
						x.corpSupplierCode === newProduct.corpSupplierCode &&
						x.supplierProductNumber === newProduct.corpSupplierCode
				) < 0
			) {
				getMetaProductChoicesArgs(
					getState,
					null,
					null,
					[newProduct],
					null
				).then((choices) => {
					dispatch(
						metaProductPageSlice.actions.addProductToMetaProduct({
							newProduct,
							choices,
						})
					);
					dispatch(search(searchParams));
				});
				if (state.metaProductPage.page.metaProduct.name === '') {
					const metaProductName =
						selectProductHeader(
							state,
							state.metaProductPage.page.metaProduct.products.map(
								(p) => `${p.corpSupplierCode}|${p.supplierProductNumber}`
							)[0]
						)?.description ||
						selectProductHeader(
							state,
							`${newProduct.corpSupplierCode}|${newProduct.supplierProductNumber}`
						)?.description ||
						'';
					dispatch(
						metaProductPageSlice.actions.metaProductNameChange(metaProductName)
					);
				}

				if (showGLs && productHeader.glGroup == null) {
					const loadGlParams = {
						corpSupplierCode: newProduct.corpSupplierCode,
						supplierProductNumber: newProduct.supplierProductNumber,
						distributionCenterCodes:
							state.metaProductPage.page.distributionCenters.map(
								(dc) => dc.dssiCode
							),
					};
					dispatch(loadGLs([loadGlParams]));
				}

				if (products.length === 0) {
					const recommendationsParams = {
						blueprintId,
						productIdentifiers: [
							{
								corpSupplierCode: newProduct.corpSupplierCode,
								supplierProductNumber: newProduct.supplierProductNumber,
							},
						],
					};
					dispatch(loadRecommendations(recommendationsParams));
				}

				dispatch(
					getProductUsageHistory({
						productsGroupedBySupplier: [
							{
								productNumbers: [newProduct.supplierProductNumber],
								corpSupplierCode: newProduct.corpSupplierCode,
								distributionCenterDssiCodes:
									state.metaProductPage.page.distributionCenters.map(
										(dc) => dc.dssiCode
									),
							},
						],
						blueprintId,
					})
				);
			}
		}
	}
);

export const deleteProductFromMetaProduct = createAsyncThunk<
	void,
	IProduct,
	{ state: IAppState; rejectValue: string[] | Response }
>(
	'metaProductPage/deleteProductFromMetaProduct',
	async (product: IProduct, { dispatch, getState }) => {
		let productsToDelete = [product];

		if (product.unitMeasureCode === null) {
			productsToDelete =
				getState().metaProductPage.page.metaProduct.products.filter(
					(x) =>
						x.corpSupplierCode === product.corpSupplierCode &&
						x.supplierProductNumber === product.supplierProductNumber
				);
		}
		getMetaProductChoicesArgs(
			getState,
			null,
			null,
			null,
			productsToDelete
		).then((choices) => {
			dispatch(
				metaProductPageSlice.actions.deleteProductsFromMetaProduct({
					productsToDelete,
					choices,
				})
			);
			dispatch(search(getState().metaProductPage.page.searchParams));
		});
	}
);

export const addDistributionCenterPriorityOverrideToMetaProduct =
	createAsyncThunk<
		void,
		IMetaProductDistributionCenterOverride,
		{ state: IAppState; rejectValue: string[] | Response }
	>(
		'metaProductPage/addDistributionCenterPriorityOverrideToMetaProduct',
		async (
			newOverride: IMetaProductDistributionCenterOverride,
			{ dispatch, getState }
		) => {
			const state = getState();
			let oldOverrideToRemove = null;
			const { distributionCenterPriorityOverrides } =
				state.metaProductPage.page.metaProduct;

			if (
				newOverride.overrideType ===
				IMetaProductDistributionCenterOverrideType.Boost
			) {
				const otherOverrideOnDcIndex =
					state.metaProductPage.page.metaProduct.distributionCenterPriorityOverrides.findIndex(
						(x) =>
							x.distributionCenterId === newOverride.distributionCenterId &&
							(x.supplierProductNumber !== newOverride.supplierProductNumber ||
								(x.supplierProductNumber ===
									newOverride.supplierProductNumber &&
									x.unitMeasureCode !== newOverride.unitMeasureCode)) &&
							x.overrideType ===
								IMetaProductDistributionCenterOverrideType.Boost
					);
				if (otherOverrideOnDcIndex >= 0) {
					oldOverrideToRemove =
						state.metaProductPage.page.metaProduct
							.distributionCenterPriorityOverrides[otherOverrideOnDcIndex];
				}
			}
			if (
				getMetaProductDistributionCenterPriorityOverrideIndex(
					distributionCenterPriorityOverrides,
					newOverride
				) < 0
			) {
				getMetaProductChoicesArgs(
					getState,
					newOverride,
					oldOverrideToRemove
				).then((choices) =>
					dispatch(
						metaProductPageSlice.actions.addDistributionCenterPriorityOverrideToMetaProduct(
							{ newOverride, oldOverrideToRemove, choices }
						)
					)
				);
			}
		}
	);

export const removeDistributionCenterPriorityOverrideFromMetaProduct =
	createAsyncThunk<
		void,
		IMetaProductDistributionCenterOverride,
		{ state: IAppState; rejectValue: string[] | Response }
	>(
		'metaProductPage/removeDistributionCenterPriorityOverrideFromMetaProduct',
		async (
			override: IMetaProductDistributionCenterOverride,
			{ dispatch, getState }
		) => {
			getMetaProductChoicesArgs(getState, null, override).then((choices) =>
				dispatch(
					metaProductPageSlice.actions.removeDistributionCenterPriorityOverrideFromMetaProduct(
						{ override, choices }
					)
				)
			);
		}
	);

function arraysAreEqual(a: IProduct[], b: IProduct[]) {
	if (a.length === b.length) {
		for (let i = 0; i < a.length; i++) {
			if (
				a[i].corpSupplierCode !== b[i].corpSupplierCode ||
				a[i].supplierProductNumber !== b[i].supplierProductNumber ||
				a[i].unitMeasureCode !== b[i].unitMeasureCode
			) {
				return false;
			}
		}
		return true;
	}
	return false;
}

export interface GetProductDetailsParams {
	supplierCode: string;
	productNumber: string;
	unitMeasureCode: string;
}

export const fetchActiveLocations = createAsyncThunk<
	ILocation[],
	number,
	{ state: IAppState; rejectValue: string[] | Response }
>(
	'metaProductPage/fetchActiveLocations',
	async (blueprintId: number, { rejectWithValue }) => {
		const res =
			await Hub.GetActiveLocationsForBlueprintV1.GetActiveLocationsForBlueprint(
				blueprintId
			);

		if (res.kind !== 'success') {
			if (res.kind === 'failure') {
				return rejectWithValue(res.errors);
			} else if (res.kind === 'error') {
				return rejectWithValue(res.response);
			} else {
				return rejectWithValue([res.ex.message]);
			}
		}

		return res.value;
	}
);

export const loadAvailability = createAsyncThunk<void, void>(
	'metaProductPage/loadAvailability',
	(_, { dispatch }) => {
		Promise.all([
			dispatch(loadSelectedAndRecommendationsAvailability()),
			dispatch(loadSearchAvailability()),
		]);
	}
);

export const loadSelectedAndRecommendationsAvailability = createAsyncThunk<
	AvailabilityResultState,
	void,
	{ state: IAppState; rejectValue: string[] | Response }
>(
	'metaProductPage/loadSelectedAndRecommendationsAvailability',
	async (_, { dispatch, getState, rejectWithValue }) => {
		const state = getState();
		const {
			blueprintId,
			selectedLocationId,
			enabledLocations,
			recommendedProducts,
			metaProduct: { products },
		} = state.metaProductPage.page;

		if (selectedLocationId <= 0) {
			return;
		}
		const allProducts = [...products, ...recommendedProducts];
		const productRequests = allProducts.reduce((acc, p) => {
			const prices = selectProductHeaderPrice(
				state,
				`${p.corpSupplierCode}|${p.supplierProductNumber}`
			)?.prices;
			if (prices) {
				return [
					...acc,
					...prices.map((price) => ({
						supplierProductNumber: p.supplierProductNumber,
						unitOfMeasure: price?.unitOfMeasure || null,
					})),
				];
			} else {
				return [
					...acc,
					{
						supplierProductNumber: p.supplierProductNumber,
						unitOfMeasure: null,
					},
				];
			}
		}, []);
		const locationCode = enabledLocations.find(
			(x) => x.id === selectedLocationId
		).locationCode;
		const result = await dispatch(
			fetchProductAvailabilities({
				blueprintId,
				locationCode,
				products: productRequests,
			})
		);

		if (fetchProductAvailabilities.fulfilled.match(result)) {
			return result.payload.status;
		} else {
			const errorMessage: IToastMessage = {
				title: 'Error',
				body: 'Something went wrong with your request',
			};
			toastr.error(errorMessage);
			return rejectWithValue(result.payload);
		}
	}
);

export const loadSearchAvailability = createAsyncThunk<
	AvailabilityResultState,
	void,
	{ state: IAppState; rejectValue: string[] | Response }
>(
	'metaProductPage/loadSearchAvailability',
	async (_, { dispatch, getState, rejectWithValue }) => {
		const state = getState();
		const {
			blueprintId,
			selectedLocationId,
			enabledLocations,
			productSearchResults,
		} = state.metaProductPage.page;

		if (selectedLocationId <= 0) {
			return;
		}
		const productRequests = productSearchResults.reduce((acc, p) => {
			const prices = selectProductHeaderPrice(
				state,
				`${p.corpSupplierCode}|${p.supplierProductNumber}`
			)?.prices;
			if (prices) {
				return [
					...acc,
					...prices.map((price) => ({
						supplierProductNumber: p.supplierProductNumber,
						unitOfMeasure: price.unitOfMeasure,
					})),
				];
			} else {
				return acc;
			}
		}, []);
		const locationCode = enabledLocations.find(
			(x) => x.id === selectedLocationId
		).locationCode;
		const result = await dispatch(
			fetchProductAvailabilities({
				blueprintId,
				locationCode,
				products: productRequests,
			})
		);

		if (fetchProductAvailabilities.fulfilled.match(result)) {
			return result.payload.status;
		} else {
			const errorMessage: IToastMessage = {
				title: 'Error',
				body: 'Something went wrong with your request',
			};
			toastr.error(errorMessage);
			return rejectWithValue(result.payload);
		}
	}
);

export const metaProductSelectionRuleSetChange = createAsyncThunk<
	void,
	number,
	{ state: IAppState }
>(
	'metaProductPage/metaProductSelectionRuleSetChange',
	async (selectionRuleSetId, { dispatch, getState }) => {
		const { searchParams } = getState().metaProductPage.page;
		const allowedStockTypes = selectionRuleToSearchOptions(
			getState(),
			selectionRuleSetId
		);
		const newSearchParams = {
			...searchParams,
			allowedStockTypes,
			pageIndex: 0,
		};
		getMetaProductChoicesArgs(
			getState,
			null,
			null,
			null,
			null,
			selectionRuleSetId
		).then((choices) =>
			dispatch(
				metaProductPageSlice.actions.metaProductSelectionRuleSetChange({
					selectionRuleSetId,
					choices,
				})
			)
		);
		await dispatch(search(newSearchParams));
	}
);

export const metaProductMaintainedByChange = createAsyncThunk<
	void,
	number,
	{ state: IAppState }
>(
	'metaProductPage/metaProductMaintainedByChange',
	async (maintainedById, { dispatch }) => {
		await dispatch(
			metaProductPageSlice.actions.metaProductMaintainedByChange(maintainedById)
		);
	}
);

export const metaProductIsSelectByUmChange = createAsyncThunk<
	void,
	boolean,
	{ state: IAppState }
>(
	'metaProductPage/metaProductIsSelectByUmChange',
	async (isSelectByUm, { dispatch, getState }) => {
		if (
			getState().metaProductPage.page.metaProduct.isSelectByUm != isSelectByUm
		) {
			await dispatch(
				metaProductPageSlice.actions.showMetaProductIsSelectByUmWarningModal()
			);
			await dispatch(
				metaProductIsSelectByUmWarningModalSlice.showMetaProductIsSelectByUmWarningModal(
					isSelectByUm
				)
			);
		}
	}
);

export const productsReorder = createAsyncThunk<
	void,
	IProduct[],
	{ state: IAppState }
>(
	'metaProductPage/productsReorder',
	async (products, { dispatch, getState }) => {
		const loadingMetaProduct = Object.assign(
			{},
			getState().metaProductPage.page.metaProduct
		);
		loadingMetaProduct.products = products;
		getMetaProductChoicesArgs(
			getState,
			null,
			null,
			null,
			null,
			loadingMetaProduct.selectionRuleSetId,
			loadingMetaProduct
		).then((choices) =>
			dispatch(
				metaProductPageSlice.actions.productsReorder({ products, choices })
			)
		);
	}
);

export const addProductsToMetaProduct = createAsyncThunk<
	void,
	{ productHeaders: IProductHeader[]; isSelectByUm: boolean },
	{ state: IAppState; rejectValue: string[] | Response }
>(
	'metaProductPage/addProductsToMetaProduct',
	async (
		args: { productHeaders: IProductHeader[]; isSelectByUm: boolean },
		{ dispatch, getState }
	) => {
		if (args.productHeaders.length === 0) {
			return;
		}

		const state = getState();
		const currentProducts: IProduct[] =
			state.metaProductPage.page.metaProduct.products;
		const productsToAdd: IProduct[] = [];
		const productHeadersToAdd: IProductHeader[] = [];
		if (args.isSelectByUm) {
			args.productHeaders.forEach((p) => {
				p.unitMeasureCodes.forEach((um) => {
					if (
						currentProducts.findIndex(
							(x) =>
								x.corpSupplierCode === p.corpSupplierCode &&
								x.supplierProductNumber === p.supplierProductNumber &&
								x.unitMeasureCode === um
						) < 0
					) {
						productsToAdd.push({
							corpSupplierCode: p.corpSupplierCode,
							supplierProductNumber: p.supplierProductNumber,
							unitMeasureCode: um,
						});
					}
				});
				if (
					productsToAdd
						.map((x) => x.supplierProductNumber)
						.findIndex((x) => x === p.supplierProductNumber) >= 0
				) {
					productHeadersToAdd.push(p);
				}
			});
		} else {
			args.productHeaders.forEach((p) => {
				if (
					currentProducts.findIndex(
						(x) =>
							x.corpSupplierCode === p.corpSupplierCode &&
							x.supplierProductNumber === p.supplierProductNumber
					) < 0
				) {
					productsToAdd.push({
						corpSupplierCode: p.corpSupplierCode,
						supplierProductNumber: p.supplierProductNumber,
						unitMeasureCode: null,
					});
					productHeadersToAdd.push(p);
				}
			});
		}

		const { blueprintId } = state.metaProductPage.page;
		const allDcs = state.metaProductPage.page.distributionCenters.map(
			(dc) => dc.dssiCode
		);

		const choicesPromise = getMetaProductChoicesArgs(
			getState,
			null,
			null,
			productsToAdd,
			null
		);

		if (state.metaProductPage.page.metaProduct.name === '') {
			const metaProductName =
				productHeadersToAdd.find(
					(x) =>
						x.corpSupplierCode === productsToAdd[0].corpSupplierCode &&
						x.supplierProductNumber === productsToAdd[0].supplierProductNumber
				).description || '';
			dispatch(
				metaProductPageSlice.actions.metaProductNameChange(metaProductName)
			);
		}

		const { searchParams, showGLs } = state.metaProductPage.page;

		const productsToRetrieveGlsFor = productHeadersToAdd.filter(
			(product) => product.glGroup == null
		);
		if (showGLs && productsToRetrieveGlsFor.length > 0) {
			const loadGlParams = productsToRetrieveGlsFor.map((p) => {
				return {
					corpSupplierCode: p.corpSupplierCode,
					supplierProductNumber: p.supplierProductNumber,
					distributionCenterCodes: allDcs,
				};
			});
			dispatch(loadGLs(loadGlParams));
		}

		if (currentProducts.length === 0) {
			const recommendationsParams = {
				blueprintId,
				productIdentifiers: [
					{
						corpSupplierCode: productsToAdd[0].corpSupplierCode,
						supplierProductNumber: productsToAdd[0].supplierProductNumber,
					},
				],
			};
			dispatch(loadRecommendations(recommendationsParams));
		}

		dispatch(search(searchParams));

		const choices = await choicesPromise;
		dispatch(
			metaProductPageSlice.actions.addProductsToMetaProduct({
				productsToAdd,
				choices,
			})
		);
	}
);

export const addProductsBySupc = createAsyncThunk<
	void,
	{ productHeaders: IProductHeader[]; isSelectByUm: boolean },
	{ state: IAppState; rejectValue: string[] | Response }
>(
	'metaProductPage/addProductsBySupc',
	async (
		args: { productHeaders: IProductHeader[]; isSelectByUm: boolean },
		{ dispatch }
	) => {
		dispatch(addProductsToMetaProduct(args));

		dispatch(metaProductPageSlice.actions.hideAddBySupcModal());

		await dispatch(
			fetchSelectedHeaderPrices(
				args.productHeaders.map((x) => x.supplierProductNumber)
			)
		);
	}
);

export const addToSaveAndOptimizeMetaproduct = createAsyncThunk<
	void,
	IAddToSaveOptimizeData,
	{ state: IAppState; rejectValue: string[] | Response }
>(
	'metaProductPage/addProductsBySupcAndSave',
	async (args: IAddToSaveOptimizeData, { dispatch }) => {
		await dispatch(
			addProductsToMetaProduct({
				productHeaders: args.headers,
				isSelectByUm: args.isSelectByUm,
			})
		);
		await dispatch(
			onSaveMetaProduct({
				isAddAndOptimize: args.isAddAndOptimize,
				routedFromOgmAi: false,
				toastMessage: args.toastMessage,
			})
		);
		await dispatch(
			SuggestionsSlice.recalculateOptimization(args.RecalculateOptimizationArgs)
		);
		await dispatch(
			fetchSelectedHeaderPrices(
				args.headers.map((x) => x.supplierProductNumber)
			)
		);
	}
);

export const showAddProductsBySupcModal = createAsyncThunk<
	void,
	void,
	{ state: IAppState; rejectValue: string[] | Response }
>(
	'metaProductPage/showAddProductsBySupcModal',
	async (_, { dispatch, getState }) => {
		const state: IAppState = getState();
		dispatch(metaProductPageSlice.actions.showAddBySupcModal());
		dispatch(addBySupcModalSlice.open(state.metaProductPage.page.blueprintId));
	}
);

export const hideAddProductBySupcModal = createAsyncThunk<
	void,
	void,
	{ state: IAppState; rejectValue: string[] | Response }
>('metaProductPage/hideAddProductsBySupcModal', async (_, { dispatch }) => {
	dispatch(metaProductPageSlice.actions.hideAddBySupcModal());
});

export const onMetaProductIsSelectByUmWarningModalPreviewClicked =
	createAsyncThunk<
		void,
		void,
		{ state: IAppState; rejectValue: string[] | Response }
	>(
		'metaProductPage/onMetaProductIsSelectByUmWarningModalPreviewClicked',
		async (_, { dispatch, getState }) => {
			const state = getState();
			const isSelectByUm =
				state.metaProductIsSelectByUmWarningModal.valueSelectedForIsSelectByUm;
			const products = state.metaProductPage.page.metaProduct.products;

			dispatch(
				metaProductPageSlice.actions.metaProductIsSelectByUmChange(isSelectByUm)
			);
			dispatch(
				metaProductIsSelectByUmWarningModalSlice.hideMetaProductIsSelectByUmWarningModal()
			);

			if (products.length > 0) {
				const productHeaders = state.productHeaders.entities;
				const productsToAdd: IProduct[] = [];

				products.forEach((p) => {
					if (isSelectByUm) {
						const productHeader =
							productHeaders[
								`${p.corpSupplierCode}|${p.supplierProductNumber}`
							];
						const mappedProductsToAdd: IProduct[] =
							productHeader.unitMeasureCodes.map((um) => ({
								corpSupplierCode: p.corpSupplierCode,
								supplierProductNumber: p.supplierProductNumber,
								unitMeasureCode: um,
							}));
						productsToAdd.push(...mappedProductsToAdd);
					} else {
						if (
							!productsToAdd.some(
								(x) =>
									x.corpSupplierCode === p.corpSupplierCode &&
									x.supplierProductNumber === p.supplierProductNumber
							)
						) {
							productsToAdd.push({
								corpSupplierCode: p.corpSupplierCode,
								supplierProductNumber: p.supplierProductNumber,
								unitMeasureCode: null,
							});
						}
					}
				});

				getMetaProductChoicesArgs(
					getState,
					null,
					null,
					productsToAdd,
					products
				).then((choices) => {
					dispatch(
						metaProductPageSlice.actions.deleteProductsFromMetaProduct({
							productsToDelete: products,
							choices,
						})
					);
					dispatch(
						metaProductPageSlice.actions.addProductsToMetaProduct({
							productsToAdd,
							choices,
						})
					);
				});
			}
		}
	);

export const hideMetaProductIsSelectByUmWarningModal = createAsyncThunk<
	void,
	void,
	{ state: IAppState; rejectValue: string[] | Response }
>(
	'metaProductPage/hideMetaProductIsSelectByUmWarningModal',
	async (_, { dispatch }) => {
		dispatch(
			metaProductPageSlice.actions.hideMetaProductIsSelectByUmWarningModal()
		);
	}
);

export const setGridViewDisplayDropDown = createAsyncThunk<
	void,
	GridViewDisplayType,
	{ state: IAppState; rejectValue: string[] | Response }
>(
	'metaProductPage/setGridViewDisplayDropDown',
	async (newDisplayType: GridViewDisplayType, { dispatch }) => {
		if (
			newDisplayType === GridViewDisplayType.price ||
			newDisplayType === GridViewDisplayType.pricePerUnit
		) {
			dispatch(fetchPricingForGridView());
		}

		dispatch(
			metaProductPageSlice.actions.setGridViewDisplayDropDown(newDisplayType)
		);
	}
);

export const fetchPricingForGridView = createAsyncThunk<
	void,
	null,
	{ state: IAppState; rejectValue: string[] | Response }
>(
	'metaProductPage/fetchPricingForGridView',
	async (_, { getState, dispatch }) => {
		const state = getState();
		const {
			metaProduct: { products },
			distributionCenters: distributionCenterCodes,
			blueprintId,
		} = state.metaProductPage.page;
		const productsWithoutLinePricing: IProductHeader[] = [];

		for (const product of products) {
			const productHeader = selectProductHeader(
				state,
				`${product.corpSupplierCode}|${product.supplierProductNumber}`
			);
			const linePricing = selectProductLinePrices(
				state,
				distributionCenterCodes.map((dc) =>
					createProductLineId(
						productHeader.corpSupplierCode,
						dc.dssiCode,
						productHeader.supplierProductNumber
					)
				)
			);
			if (linePricing.filter((line) => !line)) {
				productsWithoutLinePricing.push(productHeader);
			}
		}

		if (productsWithoutLinePricing.length > 0) {
			dispatch(
				fetchProductLines({
					blueprintId,
					productHeaders: productsWithoutLinePricing,
				})
			);
		}
	}
);

export const toggleProductViewTab = createAsyncThunk<
	void,
	string,
	{ state: IAppState; rejectValue: string[] | Response }
>(
	'metaProductPage/toggleProductViewTabVisible',
	async (newTab: string, { getState, dispatch }) => {
		const state = getState();
		const { productViewTabSelected, gridViewDisplayType } =
			state.metaProductPage.page;
		if (productViewTabSelected !== newTab) {
			if (
				(newTab === ProductViewTab.coverage &&
					gridViewDisplayType === GridViewDisplayType.price) ||
				gridViewDisplayType === GridViewDisplayType.pricePerUnit
			) {
				dispatch(fetchPricingForGridView());
			}
		}
		dispatch(metaProductPageSlice.actions.toggleProductViewTabVisible(newTab));
	}
);

const getMetaProductChoicesArgs = async (
	getState: () => IAppState,
	overrideToAdd: IMetaProductDistributionCenterOverride = null,
	overrideToRemove: IMetaProductDistributionCenterOverride = null,
	productsToAdd: IProduct[] = null,
	productsToRemove: IProduct[] = null,
	newSelectionRulesetId: number = null,
	loadedMetaProduct: IMetaProduct = null
): Promise<Hub.GetMetaProductChoicesGivenProductsAndRuleSetResponse> => {
	const state = getState();
	const metaProduct = loadedMetaProduct
		? loadedMetaProduct
		: state.metaProductPage.page.metaProduct;
	const blueprintId: number = state.metaProductPage.page.blueprintId;
	const isSelectByUm: boolean = metaProduct.isSelectByUm;

	const currentProducts: IProduct[] = metaProduct?.products
		? Object.assign([], metaProduct.products)
		: [];

	if (productsToAdd?.length > 0) {
		productsToAdd.forEach((prod) => currentProducts.push(prod));
	}
	if (productsToRemove?.length > 0) {
		productsToRemove.forEach((prod) => remove(currentProducts, prod));
	}

	const selectionRuleSetId: number = newSelectionRulesetId
		? newSelectionRulesetId
		: metaProduct?.selectionRuleSetId;

	let distributionCenterPriorityOverrides =
		metaProduct?.distributionCenterPriorityOverrides
			? Object.assign([], metaProduct.distributionCenterPriorityOverrides)
			: [];

	if (overrideToAdd) {
		distributionCenterPriorityOverrides =
			distributionCenterPriorityOverrides.filter(
				(dcpo) =>
					dcpo.distributionCenterId !== overrideToAdd.distributionCenterId ||
					dcpo.supplierProductNumber !== overrideToAdd.supplierProductNumber ||
					dcpo.unitMeasureCode !== overrideToAdd.unitMeasureCode
			);

		distributionCenterPriorityOverrides.push(overrideToAdd);
	}

	if (overrideToRemove) {
		distributionCenterPriorityOverrides =
			distributionCenterPriorityOverrides.filter(
				(dcpo) =>
					dcpo.distributionCenterId !== overrideToRemove.distributionCenterId ||
					dcpo.supplierProductNumber !==
						overrideToRemove.supplierProductNumber ||
					dcpo.unitMeasureCode !== overrideToRemove.unitMeasureCode
			);
	}

	const metaProductItems = currentProducts.map((p, index) =>
		getMetaProductOverrides(
			p.supplierProductNumber,
			p.unitMeasureCode,
			index,
			distributionCenterPriorityOverrides
		)
	);

	const request: Hub.GetMetaProductChoicesGivenProductsAndSelectionRuleSetRequest =
		{
			blueprintId,
			selectionRuleSetId,
			metaProductItems,
			isSelectByUm,
		};
	const x = unwrapFetch2(
		Hub.MetaProductController.GetMetaProductChoicesGivenProductsAndSelectionRuleSet(
			request
		)
	);
	return x;
};

const getMetaProductOverrides = (
	productNumber: string,
	unitMeasureCode: string,
	itemOrder: number,
	overrides: IMetaProductDistributionCenterOverride[]
): Hub.MetaProductItemWithOverride => {
	const metaProductWithOverrides: Hub.MetaProductItemWithOverride = {
		productNumber,
		unitMeasureCode,
		itemOrder,
		distributionCenterOverrides: null,
	};

	metaProductWithOverrides.distributionCenterOverrides = overrides
		.filter(
			(x) =>
				x.supplierProductNumber === productNumber &&
				x.unitMeasureCode === unitMeasureCode
		)
		.map((ovr): Hub.DistributionCenterOverride => {
			return {
				supplierProductNumber: ovr.supplierProductNumber,
				distributionCenterId: ovr.distributionCenterId,
				unitMeasureCode: ovr.unitMeasureCode,
				overrideType:
					Hub.OverrideType[
						IMetaProductDistributionCenterOverrideType[ovr.overrideType]
					],
			};
		});
	return metaProductWithOverrides;
};

export const requestLocationLevelPricingReport = createAsyncThunk<
	void,
	{ corpSupplierCode: string; productNumber: string; um: string },
	{ state: IAppState }
>(
	'metaProductPage/requestLocationLevelPricingReport',
	async (
		args: { corpSupplierCode: string; productNumber: string; um: string },
		{ getState }
	) => {
		try {
			const state = getState();
			const supplier =
				state.suppliers.customerSuppliersByCode[args.corpSupplierCode];
			const params: Hub.RequestLocationLevelPricingReportArgs = {
				supplierId: supplier?.id,
				supplierProductNumber: args.productNumber,
				unitOfMeasure: args.um,
			};
			const result = await unwrapFetch2(
				Hub.ReportController.RequestLocationLevelPricingReport(params)
			);
			if (result.errors.length === 0) {
				reportSuccessfullyRequested();
			} else {
				unknownErrorRequestingReport();
			}
		} catch (err: any) {
			if (err.message.includes('report-in-progress')) {
				reportCurrentlyInProgress();
			} else {
				unknownErrorRequestingReport();
			}
		}
	}
);

export const onCheckSageSelectProducts = createAsyncThunk<
	void,
	string[],
	{ state: IAppState }
>(
	'metaProductPage/onCheckSageSelectProducts',
	async (selectedIds: string[], { dispatch }) => {
		dispatch(
			metaProductPageSlice.actions.setCheckedSageSelectProducts(selectedIds)
		);
	}
);

const unknownErrorRequestingReport = () => {
	toastr.error({
		title: 'Report Request Failed',
		body: 'The report was not successfully requested.',
	});
};
const reportCurrentlyInProgress = () => {
	toastr.warning({
		title: 'Report Request Failed',
		body: 'Another report request is currently in progress',
	});
};
const reportSuccessfullyRequested = () => {
	toastr.success({
		title: 'Report Request Sent',
		body: 'The report was successfully requested.',
	});
};

export const metaProductPageSlice = createSlice({
	name: 'metaProductPage',
	initialState,
	reducers: {
		showGlAssignmentModal: (state) => {
			state.showGlAssignmentModal = true;
		},
		hideGlAssignmentModal: (state) => {
			state.showGlAssignmentModal = false;
		},
		showPhaseOutAlternatesModal: (state, action) => {
			state.phaseOutAlternatesProductHeader = action.payload;
			state.showPhaseOutAlternatesModal = true;
			state.phaseOutAlternatesSelectedProducts = [];
		},
		hidePhaseOutAlternatesModal: (state) => {
			state.phaseOutAlternatesProductHeader = undefined;
			state.showPhaseOutAlternatesModal = false;
			state.phaseOutAlternatesSelectedProducts = [];
		},
		addProductToPhaseOutAlternatesSelectedProducts: (state, action) => {
			if (
				state.phaseOutAlternatesSelectedProducts.indexOf(action.payload) === -1
			) {
				state.phaseOutAlternatesSelectedProducts.push(action.payload);
			}
		},
		removeProductToPhaseOutAlternatesSelectedProducts: (state, action) => {
			state.phaseOutAlternatesSelectedProducts =
				state.phaseOutAlternatesSelectedProducts.filter(
					(p) => p !== action.payload
				);
		},
		showAddBySupcModal: (state) => {
			state.showAddBySupcModal = true;
		},
		hideAddBySupcModal: (state) => {
			state.showAddBySupcModal = false;
		},
		showMetaProductIsSelectByUmWarningModal: (state) => {
			state.showMetaProductIsSelectByUmWarningModal = true;
		},
		hideMetaProductIsSelectByUmWarningModal: (state) => {
			state.showMetaProductIsSelectByUmWarningModal = false;
		},
		toggleExpandedDescriptions: (state) => {
			state.showExpandedDescriptions = !state.showExpandedDescriptions;
		},
		toggleRebate: (state) => {
			state.rebateApplied = !state.rebateApplied;
			LocalStorage.setShowEstNetPrice(state.rebateApplied);
		},
		toggleProductAttributes: (state) => {
			state.showProductAttributes = !state.showProductAttributes;
			LocalStorage.setShowProductAttributes(state.showProductAttributes);
		},
		toggleShowWeightInOunces: (state) => {
			state.showWeightInOunces = !state.showWeightInOunces;
			LocalStorage.setShowWeightInOunces(state.showWeightInOunces);
		},
		toggleProductViewTabVisible: (state, action) => {
			state.productViewTabSelected = action.payload;
		},
		setGridViewDisplayDropDown: (state, action) => {
			state.gridViewDisplayType = action.payload;
		},
		toggleCategoriesAndGLs: (state) => {
			state.showCategories = !state.showCategories;
			state.showGLs = state.isDssiSiteType && !state.showGLs;
		},
		addProductToMetaProduct: (state, action) => {
			const newProduct = action.payload.newProduct;
			const isSelectByUm = state.metaProduct.isSelectByUm;
			const newProductKey = isSelectByUm
				? `${newProduct.corpSupplierCode}|${newProduct.supplierProductNumber}|${newProduct.unitMeasureCode}`
				: `${newProduct.corpSupplierCode}|${newProduct.supplierProductNumber}`;
			if (isSelectByUm) {
				if (
					state.metaProduct.products.findIndex(
						(x) =>
							x.corpSupplierCode === newProduct.corpSupplierCode &&
							x.supplierProductNumber === newProduct.supplierProductNumber &&
							x.unitMeasureCode === newProduct.unitMeasureCode
					) < 0
				) {
					state.metaProduct.products.push({
						corpSupplierCode: newProduct.corpSupplierCode,
						supplierProductNumber: newProduct.supplierProductNumber,
						unitMeasureCode: newProduct.unitMeasureCode,
					});
					state.metaProduct.productSpecIndicatorIds[newProductKey] = 0;
					state.metaProduct.choices = action.payload.choices;
					state.isPageDirty = true;
				}
			} else {
				if (
					state.metaProduct.products.findIndex(
						(x) =>
							x.corpSupplierCode === newProduct.corpSupplierCode &&
							x.supplierProductNumber === newProduct.supplierProductNumber
					) < 0
				) {
					state.metaProduct.products.push({
						corpSupplierCode: newProduct.corpSupplierCode,
						supplierProductNumber: newProduct.supplierProductNumber,
						unitMeasureCode: newProduct.unitMeasureCode,
					});
					state.metaProduct.productSpecIndicatorIds[newProductKey] = 0;
					state.metaProduct.choices = action.payload.choices;
					state.isPageDirty = true;
				}
			}
		},
		addProductsToMetaProduct: (state, action) => {
			const isSelectByUm = state.metaProduct.isSelectByUm;
			const newProducts: IProduct[] = action.payload.productsToAdd.filter((p) =>
				isSelectByUm
					? state.metaProduct.products.findIndex(
							(x) =>
								x.corpSupplierCode === p.corpSupplierCode &&
								x.supplierProductNumber === p.supplierProductNumber &&
								x.unitMeasureCode === p.unitMeasureCode
					  ) < 0
					: state.metaProduct.products.findIndex(
							(x) =>
								x.corpSupplierCode === p.corpSupplierCode &&
								x.supplierProductNumber === p.supplierProductNumber
					  ) < 0
			);
			if (newProducts.length > 0) {
				state.metaProduct.products.push(...newProducts);
				newProducts.forEach((product) => {
					const id = isSelectByUm
						? `${product.corpSupplierCode}|${product.supplierProductNumber}|${product.unitMeasureCode}`
						: `${product.corpSupplierCode}|${product.supplierProductNumber}`;
					state.metaProduct.productSpecIndicatorIds[id] = 0;
				});
				state.isPageDirty = true;
			}
			state.metaProduct.choices = action.payload.choices;
		},
		deleteProductFromMetaProduct: (state, action) => {
			const productToRemove = action.payload.product;
			const index = state.metaProduct.products.findIndex(
				(product) =>
					product.corpSupplierCode === productToRemove.corpSupplierCode &&
					product.supplierProductNumber ===
						productToRemove.supplierProductNumber
			);
			if (index > -1) {
				const metaProductItemId = state.metaProduct.isSelectByUm
					? `${productToRemove.corpSupplierCode}|${productToRemove.supplierProductNumber}|${productToRemove.unitMeasureCode}`
					: `${productToRemove.corpSupplierCode}|${productToRemove.supplierProductNumber}`;
				state.metaProduct.products.splice(index, 1);
				delete state.metaProduct.productSpecIndicatorIds[metaProductItemId];
				state.metaProduct.choices = action.payload.choices;
				state.isPageDirty = true;
			}
		},
		deleteProductsFromMetaProduct: (state, action) => {
			const productsToRemove = action.payload.productsToDelete;
			let productWasRemoved = false;
			productsToRemove.forEach((product) => {
				const index = state.metaProduct.products.findIndex(
					(p) =>
						p.corpSupplierCode === product.corpSupplierCode &&
						p.supplierProductNumber === product.supplierProductNumber &&
						p.unitMeasureCode === product.unitMeasureCode
				);

				if (index > -1) {
					state.metaProduct.products.splice(index, 1);
					const id = state.metaProduct.isSelectByUm
						? `${product.corpSupplierCode}|${product.supplierProductNumber}|${product.unitMeasureCode}`
						: `${product.corpSupplierCode}|${product.supplierProductNumber}`;
					delete state.metaProduct.productSpecIndicatorIds[id];
					productWasRemoved = true;
				}
			});

			if (productWasRemoved) {
				state.metaProduct.choices = action.payload.choices;
				state.isPageDirty = true;
			}
		},
		addDistributionCenterPriorityOverrideToMetaProduct: (state, action) => {
			const indexToAdd =
				state.metaProduct.distributionCenterPriorityOverrides.findIndex(
					(override) =>
						override.distributionCenterId ===
							action.payload?.newOverride?.distributionCenterId &&
						override.supplierProductNumber ===
							action.payload?.newOverride?.supplierProductNumber &&
						override.unitMeasureCode ===
							action.payload?.newOverride?.unitMeasureCode
				);
			if (indexToAdd < 0) {
				state.metaProduct.distributionCenterPriorityOverrides.push(
					action.payload.newOverride
				);
				state.isPageDirty = true;
			} else {
				state.metaProduct.distributionCenterPriorityOverrides.splice(
					indexToAdd,
					1
				);
				state.metaProduct.distributionCenterPriorityOverrides.push(
					action.payload.newOverride
				);
				state.isPageDirty = true;
			}
			const indexToRemove =
				state.metaProduct.distributionCenterPriorityOverrides.findIndex(
					(override) =>
						override.distributionCenterId ===
							action.payload?.oldOverrideToRemove?.distributionCenterId &&
						override.supplierProductNumber ===
							action.payload?.oldOverrideToRemove?.supplierProductNumber &&
						override.unitMeasureCode ===
							action.payload?.oldOverrideToRemove?.unitMeasureCode
				);
			if (indexToRemove >= 0) {
				state.metaProduct.distributionCenterPriorityOverrides.splice(
					indexToRemove,
					1
				);
				state.isPageDirty = true;
			}
			state.metaProduct.choices = action.payload.choices;
		},
		removeDistributionCenterPriorityOverrideFromMetaProduct: (
			state,
			action
		) => {
			const index =
				state.metaProduct.distributionCenterPriorityOverrides.findIndex(
					(override) =>
						override.distributionCenterId ===
							action.payload.override.distributionCenterId &&
						override.supplierProductNumber ===
							action.payload.override.supplierProductNumber &&
						override.unitMeasureCode === action.payload.override.unitMeasureCode
				);
			if (index >= 0) {
				state.metaProduct.distributionCenterPriorityOverrides.splice(index, 1);
				state.isPageDirty = true;
			}
			state.metaProduct.choices = action.payload.choices;
		},
		metaProductNameChange: (state, action) => {
			if (state.metaProduct.name !== action.payload) {
				state.metaProduct.name = action.payload;
				state.isPageDirty = true;
			}
		},
		metaProductSelectionRuleSetChange: (state, action) => {
			if (
				state.metaProduct.selectionRuleSetId !==
				action.payload.selectionRuleSetId
			) {
				state.metaProduct.selectionRuleSetId =
					action.payload.selectionRuleSetId;
				state.isPageDirty = true;
			}
			state.metaProduct.choices = action.payload.choices;
		},
		metaProductMaintainedByChange: (state, action) => {
			if (state.metaProduct.maintainedBy !== action.payload) {
				state.metaProduct.maintainedBy = action.payload;
				state.isPageDirty = true;
			}
		},
		metaProductIsSelectByUmChange: (state, action) => {
			if (state.metaProduct.isSelectByUm !== action.payload) {
				state.metaProduct.isSelectByUm = action.payload;
				state.isPageDirty = true;
				state.showMetaProductIsSelectByUmWarningModal = false;
			}
		},
		metaProductCustomCategoryChange: (state, action) => {
			if (state.metaProduct.customCategoryId !== action.payload) {
				state.metaProduct.customCategoryId = action.payload;
				state.isPageDirty = true;
			}
		},
		onlyAdditionalCoverageChange: (state, action) => {
			state.showOnlyAdditionalCoverage = action.payload;
		},
		recommendationsCoverageChange: (state, action) => {
			state.recommendationsShowOnlyAdditionalCoverage =
				action.payload.newShowOnlyAdditionalCoverage;
			state.includeOpenSpecCoverage = action.payload.newIncludeOpenSpecCoverage;
		},
		dcFilterChange: (state, action) => {
			state.selectedDcsForSearch = action.payload;
		},
		searchTermChange: (state, action) => {
			state.searchParams.searchTerm = action.payload;
			state.metaProduct.lastSearchTerm = action.payload;
		},
		searchParamsChange: (state, action) => {
			state.searchParams = action.payload;
		},
		searchSupplierUpdate: (state, action) => {
			state.searchParams.corpSupplierCodesFilter = action.payload;
		},
		toggleSearchFilterModal: (state) => {
			state.searchFilterModalOpen = !state.searchFilterModalOpen;
		},
		productsReorder: (state, action) => {
			const oldOrder = state.metaProduct.products;
			if (!arraysAreEqual(oldOrder, action.payload.products)) {
				state.metaProduct.products = action.payload.products;
				state.isPageDirty = true;
			}
			state.metaProduct.choices = action.payload.choices;
		},
		setSelectionRuleSets: (state, action) => {
			state.selectionRuleSets = action.payload;
		},
		toggleEditSpecIndicator: (state, action) => {
			state.editSpecIndicator = action.payload;
		},
		setSpecIndicatorEnable: (state, action) => {
			state.specIndicatorEnabled = action.payload;
		},
		specIndicatorSelect: (state, action) => {
			if (state.metaProduct.specIndicatorId !== action.payload) {
				state.metaProduct.specIndicatorId = action.payload;
				state.isPageDirty = true;
			}
		},
		productSpecIndicatorSelect: (state, action) => {
			const { productId, specIndicatorId } = action.payload;
			if (
				state.metaProduct.productSpecIndicatorIds[productId] !== specIndicatorId
			) {
				state.metaProduct.productSpecIndicatorIds[productId] = specIndicatorId;
				state.isPageDirty = true;
			}
		},
		selectedProductKeyChange: (state, action) => {
			state.selectedProductKey = action.payload;
		},
		availabilityLocationChange: (state, action) => {
			state.selectedLocationId = action.payload;
			state.showProductAvailability = false;
			state.productAvailabilityStatus = LoadingStatus.idle;
			state.showSearchProductAvailability = false;
			state.searchProductAvailabilityStatus = LoadingStatus.idle;
		},
		setChoices: (state, action) => {
			state.metaProduct.choices = action.payload;
		},
		setSupplierCode: (state, action) => {
			state.supplierCode = action.payload;
		},
		setCheckedSageSelectProducts: (state, action) => {
			state.checkedSageSelectProducts = action.payload;
		},
		resetCheckedSageSelectProducts: (state) => {
			state.checkedSageSelectProducts = [];
		},
		setShowAddAndOptimizeWarningModal: (state, action) => {
			state.isShowingAddAndOptimizeWarningModal = action.payload;
		},
		setDontShowAddAndOptimizeWarningModalSetting: (state, action) => {
			state.dontShowAddAndOptimizeWarningModalSetting = action.payload;
			LocalStorage.setDontShowAddAndOptimizeWarningModal(action.payload);
		},
		setDontShowAddAndOptimizeWarningModalSettingCheckbox: (state, action) => {
			state.dontShowAddAndOptimizeWarningModalSettingCheckbox = action.payload;
		},
		setIsPageDirty: (state, action) => {
			state.isPageDirty = action.payload;
		},
	},
	extraReducers: (builder) => {
		builder.addCase(loadMetaProduct.pending, (state, action) => {
			return {
				...state,
				loadingStatus: LoadingStatus.loading,
				savingStatus: initialState.savingStatus,
				isPageDirty: initialState.isPageDirty,
				metaProduct: initialState.metaProduct,
				metaProductGrouping: initialState.metaProductGrouping,
				blueprintId: action.meta.arg.blueprintId,
				customCategories: initialState.customCategories,
				searchStatus: initialState.searchStatus,
				selectedLocationId: initialState.selectedLocationId,
				showProductAvailability: initialState.showProductAvailability,
				productAvailabilityStatus: initialState.productAvailabilityStatus,
				productAvailabilityResultStatus:
					initialState.productAvailabilityResultStatus,
				showSearchProductAvailability:
					initialState.showSearchProductAvailability,
				searchProductAvailabilityStatus:
					initialState.searchProductAvailabilityStatus,
				searchProductAvailabilityResultStatus:
					initialState.searchProductAvailabilityResultStatus,
				productSearchResults: initialState.productSearchResults,
				showOnlyAdditionalCoverage:
					LocalStorage.getShowOnlyAdditionalCoverage() ?? false,
				searchTotalResults: initialState.searchTotalResults,
				productViewTabSelected: initialState.productViewTabSelected,
				searchParams: {
					...initialState.searchParams,
					includeOnlyEligibleItems:
						LocalStorage.getShowOnlyAdditionalCoverage() ?? false,
					blueprintId: action.meta.arg.blueprintId,
				},
				selectedDcsForSearch: initialState.selectedDcsForSearch,
				distributionCenters: initialState.distributionCenters,
				rebateApplied: LocalStorage.getShowEstNetPrice() ?? false,
				recommendedProducts: [],
				metaProductPartOfGroupingLine:
					initialState.metaProductPartOfGroupingLine,
				supplierCode: '',
				usFoodsProductAttributes: [],
				usFoodsLoadingStatus: initialState.usFoodsLoadingStatus,
				showProductAttributes: LocalStorage.getShowProductAttributes() ?? false,
				checkedSageSelectProducts: [],
				isShowingAddAndOptimizeWarningModal:
					initialState.isShowingAddAndOptimizeWarningModal,
				dontShowAddAndOptimizeWarningModalSetting:
					LocalStorage.getDontShowAddAndOptimizeWarningModal() ?? false,
				dontShowAddAndOptimizeWarningModalSettingCheckbox: false,
			};
		});
		builder.addCase(loadMetaProduct.rejected, (state) => {
			return {
				...state,
				loadingStatus: LoadingStatus.failure,
				metaProduct: null,
			};
		});
		builder.addCase(loadMetaProduct.fulfilled, (state, action) => {
			const allowedStockTypes = selectionRuleToSearchOptions(
				state,
				action.payload.selectionRuleSetId
			);
			return {
				...state,
				loadingStatus: LoadingStatus.success,
				metaProduct: action.payload,
				searchParams: {
					...state.searchParams,
					allowedStockTypes,
				},
			};
		});
		builder.addCase(loadGrouping.fulfilled, (state, action) => {
			return {
				...state,
				metaProductGrouping: action.payload,
			};
		});
		builder.addCase(saveMetaProduct.fulfilled, (state) => {
			return {
				...state,
				savingStatus: LoadingStatus.success,
			};
		});
		builder.addCase(saveMetaProduct.rejected, (state) => {
			return {
				...state,
				savingStatus: LoadingStatus.failure,
			};
		});
		builder.addCase(saveMetaProduct.pending, (state) => {
			return {
				...state,
				savingStatus: LoadingStatus.loading,
			};
		});
		builder.addCase(search.rejected, (state, action) => {
			const { payload } = action;
			if (payload instanceof Array && payload[0] === 'Aborted') {
				return;
			}
			state.searchStatus = LoadingStatus.failure;
			state.productSearchResults = initialState.productSearchResults;
			state.searchTotalResults = initialState.searchTotalResults;
		});
		builder.addCase(search.pending, (state) => {
			state.searchStatus = LoadingStatus.loading;
		});
		builder.addCase(search.fulfilled, (state, action) => {
			state.searchStatus = LoadingStatus.success;
			state.productSearchResults = action.payload.currentPage;
			state.searchTotalResults = action.payload.totalResults;
			state.searchFilters = {
				categoryLevel1Facet: action.payload.categoryLevel1Facet,
				categoryLevel2Facet: action.payload.categoryLevel2Facet,
				categoryLevel3Facet: action.payload.categoryLevel3Facet,
				categoryLevel4Facet: action.payload.categoryLevel4Facet,
				brandFacet: action.payload.brandFacet,
				packSizeFacet: sortPackSizeFacet(action.payload.packSizeFacet),
			};
		});

		builder.addCase(fetchProductsByProductNumber.rejected, (state) => {
			state.productNumberLookupStatus = LoadingStatus.failure;
		});
		builder.addCase(fetchProductsByProductNumber.pending, (state) => {
			state.productNumberLookupStatus = LoadingStatus.loading;
		});
		builder.addCase(fetchProductsByProductNumber.fulfilled, (state) => {
			state.productNumberLookupStatus = LoadingStatus.success;
		});

		builder.addCase(loadRecommendations.fulfilled, (state, action) => {
			state.recommendedProducts = action.payload;
		});
		builder.addCase(fetchSearchHeaderPrices.rejected, (state) => {
			state.searchPricingStatus = LoadingStatus.failure;
		});
		builder.addCase(fetchSearchHeaderPrices.pending, (state) => {
			state.searchPricingStatus = LoadingStatus.loading;
		});
		builder.addCase(fetchSearchHeaderPrices.fulfilled, (state) => {
			state.searchPricingStatus = LoadingStatus.success;
		});
		builder.addCase(fetchSelectedHeaderPrices.rejected, (state) => {
			state.selectedProductsPricingStatus = LoadingStatus.failure;
		});
		builder.addCase(fetchSelectedHeaderPrices.pending, (state) => {
			state.selectedProductsPricingStatus = LoadingStatus.loading;
		});
		builder.addCase(fetchSelectedHeaderPrices.fulfilled, (state) => {
			state.selectedProductsPricingStatus = LoadingStatus.success;
		});
		builder.addCase(fetchRecommendedHeaderPrices.rejected, (state) => {
			state.recommendationsPricingStatus = LoadingStatus.failure;
		});
		builder.addCase(fetchRecommendedHeaderPrices.pending, (state) => {
			state.recommendationsPricingStatus = LoadingStatus.loading;
		});
		builder.addCase(fetchRecommendedHeaderPrices.fulfilled, (state) => {
			state.recommendationsPricingStatus = LoadingStatus.success;
		});
		builder.addCase(fetchProductLines.rejected, (state) => {
			state.productLineStatus = LoadingStatus.failure;
		});
		builder.addCase(fetchProductLines.pending, (state) => {
			state.productLineStatus = LoadingStatus.loading;
		});
		builder.addCase(fetchProductLines.fulfilled, (state) => {
			state.productLineStatus = LoadingStatus.success;
		});
		builder.addCase(fetchLineLevelPrices.rejected, (state) => {
			state.productLinePricingStatus = LoadingStatus.failure;
		});
		builder.addCase(fetchLineLevelPrices.pending, (state) => {
			state.productLinePricingStatus = LoadingStatus.loading;
		});
		builder.addCase(fetchLineLevelPrices.fulfilled, (state) => {
			state.productLinePricingStatus = LoadingStatus.success;
		});
		builder.addCase(fetchMultipleLineLevelPrices.rejected, (state) => {
			state.productLinePricingStatus = LoadingStatus.failure;
		});
		builder.addCase(fetchMultipleLineLevelPrices.pending, (state) => {
			state.productLinePricingStatus = LoadingStatus.loading;
		});
		builder.addCase(fetchMultipleLineLevelPrices.fulfilled, (state) => {
			state.productLinePricingStatus = LoadingStatus.success;
		});
		builder.addCase(fetchUSFoodsProductAttributes.pending, (state) => {
			state.usFoodsLoadingStatus = LoadingStatus.loading;
		});
		builder.addCase(fetchUSFoodsProductAttributes.rejected, (state) => {
			state.usFoodsLoadingStatus = LoadingStatus.failure;
		});
		builder.addCase(
			fetchUSFoodsProductAttributes.fulfilled,
			(state, action) => {
				state.usFoodsLoadingStatus = LoadingStatus.success;
				state.usFoodsProductAttributes = state.usFoodsProductAttributes.concat(
					action.payload
				);
			}
		);
		builder.addCase(loadCustomCategories.fulfilled, (state, action) => {
			state.customCategories = action.payload;
		});
		builder.addCase(loadGLs.rejected, (state) => {
			state.loadGLStatus = LoadingStatus.failure;
		});
		builder.addCase(loadGLs.pending, (state) => {
			state.loadGLStatus = LoadingStatus.loading;
		});
		builder.addCase(loadGLs.fulfilled, (state) => {
			state.loadGLStatus = LoadingStatus.success;
		});
		builder.addCase(loadDistributionCenters.fulfilled, (state, action) => {
			state.distributionCenters = action.payload;
			state.searchParams.distributionCentersFilter = action.payload.map(
				(dc) => dc.dssiCode
			);
		});
		builder.addCase(fetchActiveLocations.fulfilled, (state, action) => {
			state.enabledLocations = action.payload;
		});
		builder.addCase(
			loadSelectedAndRecommendationsAvailability.pending,
			(state) => {
				state.productAvailabilityStatus = LoadingStatus.loading;
				state.showProductAvailability = false;
			}
		);
		builder.addCase(
			loadSelectedAndRecommendationsAvailability.rejected,
			(state) => {
				state.productAvailabilityStatus = LoadingStatus.failure;
				state.showProductAvailability = false;
			}
		);
		builder.addCase(
			loadSelectedAndRecommendationsAvailability.fulfilled,
			(state, action) => {
				state.productAvailabilityStatus = LoadingStatus.success;
				state.showProductAvailability = true;
				state.productAvailabilityResultStatus = action.payload;
			}
		);
		builder.addCase(loadSearchAvailability.pending, (state) => {
			state.searchProductAvailabilityStatus = LoadingStatus.loading;
			state.showSearchProductAvailability = false;
		});
		builder.addCase(loadSearchAvailability.rejected, (state) => {
			state.searchProductAvailabilityStatus = LoadingStatus.failure;
			state.showSearchProductAvailability = false;
		});
		builder.addCase(loadSearchAvailability.fulfilled, (state, action) => {
			state.searchProductAvailabilityStatus = LoadingStatus.success;
			state.showSearchProductAvailability = true;
			state.searchProductAvailabilityResultStatus = action.payload;
		});
		builder.addCase(getProductUsageHistory.pending, (state) => {
			state.productUsageStatus = LoadingStatus.loading;
		});
		builder.addCase(getProductUsageHistory.rejected, (state) => {
			state.productUsageStatus = LoadingStatus.failure;
		});
		builder.addCase(getProductUsageHistory.fulfilled, (state) => {
			state.productUsageStatus = LoadingStatus.success;
		});
		builder.addCase(getGroupingByMasterItem.pending, (state) => {
			state.metaProductPartOfGroupingLine =
				initialState.metaProductPartOfGroupingLine;
		});
		builder.addCase(getGroupingByMasterItem.fulfilled, (state, action) => {
			state.metaProductPartOfGroupingLine = action.payload;
		});
	},
});

export function selectSupplierId(state: IAppState) {
	const blueprint = selectBlueprintById(
		state,
		state.metaProductPage.page.blueprintId
	);
	return blueprint?.supplierId;
}

const getSelectedProducts = (state: IAppState) => {
	if (state.metaProductPage.page.metaProduct?.products == null) {
		return [];
	}
	return state.metaProductPage.page.metaProduct?.products;
};

const getSelectedProductHeaders = (state: IAppState) => {
	if (state.metaProductPage.page.metaProduct?.products == null) {
		return [];
	}
	return selectProductHeaders(
		state,
		state.metaProductPage.page.metaProduct.products.map(
			(p) => `${p.corpSupplierCode}|${p.supplierProductNumber}`
		)
	);
};

const getProducts = (state: IAppState, ids) => {
	if (ids == null) {
		return [];
	}
	return ids;
};

const getProductHeaders = (state: IAppState, ids) => {
	if (ids == null) {
		return [];
	}
	return selectProductHeaders(
		state,
		ids.map((p) => `${p.corpSupplierCode}|${p.supplierProductNumber}`)
	);
};

const getSelectByUm = (state: IAppState) => {
	return state.metaProductPage.page.metaProduct?.isSelectByUm;
};

const getDcs = (state: IAppState) =>
	state.metaProductPage.page?.distributionCenters || [];

const getSelectedLocationCode = (state: IAppState) => {
	const { selectedLocationId, enabledLocations } = state.metaProductPage.page;
	if (selectedLocationId <= 0) {
		return null;
	}
	return enabledLocations.find((location) => location.id === selectedLocationId)
		?.locationCode;
};

const getCoverageFilters = (state: IAppState) => {
	const selectionRuleSetId =
		state.metaProductPage.page?.metaProduct?.selectionRuleSetId;
	let coverageFilters: string[] = ['S', 'D', 'R'];
	if (selectionRuleSetId != null) {
		const displayToken = state.metaProductPage.page?.selectionRuleSets?.find(
			(x) => x.id === selectionRuleSetId
		)?.displayToken;
		coverageFilters = rulesetToFilterOptions(displayToken);
	}
	return coverageFilters;
};

function rulesetToFilterOptions(token: string): string[] {
	switch (token) {
		case 'leadtime-sdrn':
			return ['S', 'D', 'R', 'N'];
		case 'usf-leadtime':
			return ['S', 'D', 'R'];
		case 'leadtime-sdr':
			return ['S', 'D', 'R'];
		case 'leadtime-sd':
			return ['S', 'D'];
		case 'priority-sdrn':
			return ['S', 'D', 'R', 'N'];
		case 'priority-sdr':
			return ['S', 'D', 'R'];
		case 'priority-sd':
			return ['S', 'D'];
		case 'only-stocked':
			return ['S'];
		case 'only-remote':
			return ['R'];
		default:
			return [];
	}
}

function selectionRuleToSearchOptions(
	state,
	selectionRuleSetId: number
): Hub.StockType[] {
	const selectionRuleSets = Customer.customer().selectionRuleSets;
	const token = selectionRuleSets?.find(
		(x) => x.id === selectionRuleSetId
	)?.displayToken;
	switch (token) {
		case 'leadtime-sdrn':
			return [
				Hub.StockType.Stocked,
				Hub.StockType.Demand,
				Hub.StockType.Remote,
				Hub.StockType.NonStock,
			];
		case 'usf-leadtime':
			return [
				Hub.StockType.Stocked,
				Hub.StockType.Demand,
				Hub.StockType.Remote,
			];
		case 'leadtime-sdr':
			return [
				Hub.StockType.Stocked,
				Hub.StockType.Demand,
				Hub.StockType.Remote,
			];
		case 'leadtime-sd':
			return [Hub.StockType.Stocked, Hub.StockType.Demand];
		case 'priority-sdrn':
			return [
				Hub.StockType.Stocked,
				Hub.StockType.Demand,
				Hub.StockType.Remote,
				Hub.StockType.NonStock,
			];
		case 'priority-sdr':
			return [
				Hub.StockType.Stocked,
				Hub.StockType.Demand,
				Hub.StockType.Remote,
			];
		case 'priority-sd':
			return [Hub.StockType.Stocked, Hub.StockType.Demand];
		case 'only-stocked':
			return [Hub.StockType.Stocked];
		case 'only-remote':
			return [Hub.StockType.Remote];
		default:
			return User.isDssiSiteType()
				? [Hub.StockType.Stocked, Hub.StockType.Demand, Hub.StockType.Remote]
				: [
						Hub.StockType.Stocked,
						Hub.StockType.Demand,
						Hub.StockType.Remote,
						Hub.StockType.NonStock,
				  ];
	}
}

const getBlueprintId = (state: IAppState) =>
	state.metaProductPage.page.blueprintId;

const getProductAvailability = (state: IAppState) =>
	state.productAvailabilities?.entities || null;

const getProductPriorityOverrides = (state: IAppState) => {
	return (
		state.metaProductPage.page?.metaProduct
			?.distributionCenterPriorityOverrides || null
	);
};

interface makeGetProductCoverageParams {
	isSelectedProduct: boolean;
	withExclusions: boolean;
	isSelectByUm: boolean;
}

export const makeGetProductCoverage = (
	params: makeGetProductCoverageParams
) => {
	return createSelector(
		[
			getSelectedProducts,
			getSelectedProductHeaders,
			getProducts,
			getProductHeaders,
			getDcs,
			getCoverageFilters,
			getProductPriorityOverrides,
		],
		(
			selectedProducts: IProduct[],
			selectedProductHeaders: IProductHeader[],
			products: IProduct[],
			productHeaders: IProductHeader[],
			dcs: IDistributionCenter[],
			coverageTypes: string[],
			dcExcludes: IMetaProductDistributionCenterOverride[]
		) => {
			if (params.isSelectedProduct) {
				return calculateSelectedProductCoverage(
					products,
					productHeaders,
					dcs,
					coverageTypes,
					dcExcludes,
					params.withExclusions,
					params.isSelectByUm
				);
			}
			return calculateUnselectedProductCoverage(
				products,
				productHeaders,
				dcs,
				coverageTypes,
				selectedProducts,
				selectedProductHeaders,
				dcExcludes,
				params.withExclusions,
				false
			);
		}
	) as (state: IAppState, ids: IProduct[]) => IProductCoverage;
};

function calculateSelectedProductCoverage(
	products: IProduct[],
	productHeaders: IProductHeader[],
	allDcs: IDistributionCenter[],
	coverageFilters: string[],
	dcPriorityOverrides: IMetaProductDistributionCenterOverride[],
	withExclusions: boolean,
	isSelectByUm: boolean
): IProductCoverage {
	const coverage = {};
	const alldcDssiCodes = allDcs.map((dc) => dc.dssiCode);

	let dcsCoveredByProduct;
	products.forEach((product) => {
		const productHeader = productHeaders.find(
			(x) =>
				x.corpSupplierCode === product.corpSupplierCode &&
				x.supplierProductNumber === product.supplierProductNumber
		);
		if (withExclusions && dcPriorityOverrides != null) {
			dcsCoveredByProduct = getCoveredDCsForProductWithExclusions(
				product,
				productHeader,
				coverageFilters,
				allDcs,
				dcPriorityOverrides.filter(
					(dcpo) =>
						dcpo.supplierProductNumber === product.supplierProductNumber &&
						dcpo.overrideType ===
							IMetaProductDistributionCenterOverrideType.Exclude
				),
				isSelectByUm
			).filter((dc) => alldcDssiCodes.includes(dc));
		} else {
			dcsCoveredByProduct = getCoveredDCsForProduct(
				product,
				productHeader,
				coverageFilters,
				isSelectByUm
			).filter((dc) => alldcDssiCodes.includes(dc));
		}

		const coverageId =
			isSelectByUm && product.unitMeasureCode
				? `${product.corpSupplierCode}|${product.supplierProductNumber}|${product.unitMeasureCode}`
				: `${product.corpSupplierCode}|${product.supplierProductNumber}`;

		coverage[coverageId] = {
			additionalCoverageCount: 0,
			coverageFromThisProduct: dcsCoveredByProduct?.length || 0,
			coveredDCs: dcsCoveredByProduct || [],
			totalDcCount: allDcs.length || 0,
			specIndicatorId: productHeader.specIndicatorId,
		};
	});
	return coverage;
}

function calculateUnselectedProductCoverage(
	products: IProduct[],
	productHeaders: IProductHeader[],
	allDcs: IDistributionCenter[],
	coverageFilters: string[],
	selectedProducts: IProduct[],
	selectedProductHeaders: IProductHeader[],
	dcPriorityOverrides: IMetaProductDistributionCenterOverride[],
	withExclusions: boolean,
	isSelectByUm: boolean
): IProductCoverage {
	const coverage = {};
	const coveredDcs = new Set<string>();
	const alldcDssiCodes = allDcs.map((dc) => dc.dssiCode);

	selectedProducts.forEach((product) => {
		const productHeader = selectedProductHeaders.find(
			(x) =>
				x.corpSupplierCode === product.corpSupplierCode &&
				x.supplierProductNumber == product.supplierProductNumber
		);
		let productDcs;
		if (withExclusions && dcPriorityOverrides != null) {
			productDcs = getCoveredDCsForProductWithExclusions(
				product,
				productHeader,
				coverageFilters,
				allDcs,
				dcPriorityOverrides.filter(
					(dcpo) =>
						dcpo.supplierProductNumber === product.supplierProductNumber &&
						dcpo.overrideType ===
							IMetaProductDistributionCenterOverrideType.Exclude
				),
				isSelectByUm
			).filter((dc) => alldcDssiCodes.includes(dc));
		} else {
			productDcs = getCoveredDCsForProduct(
				product,
				productHeader,
				coverageFilters,
				isSelectByUm
			).filter((dc) => alldcDssiCodes.includes(dc));
		}
		productDcs.forEach((dc) => {
			coveredDcs.add(dc);
		});
	});

	products.forEach((product) => {
		const productHeader = productHeaders.find(
			(x) =>
				x.corpSupplierCode === product.corpSupplierCode &&
				x.supplierProductNumber == product.supplierProductNumber
		);
		let dcsCoveredByProduct;
		if (withExclusions && dcPriorityOverrides != null) {
			dcsCoveredByProduct = getCoveredDCsForProductWithExclusions(
				product,
				productHeader,
				coverageFilters,
				allDcs,
				dcPriorityOverrides.filter(
					(dcpo) =>
						dcpo.supplierProductNumber === product.supplierProductNumber &&
						dcpo.overrideType ===
							IMetaProductDistributionCenterOverrideType.Exclude
				),
				isSelectByUm
			).filter((dc) => alldcDssiCodes.includes(dc));
		} else {
			dcsCoveredByProduct = getCoveredDCsForProduct(
				product,
				productHeader,
				coverageFilters,
				isSelectByUm
			).filter((dc) => alldcDssiCodes.includes(dc));
		}

		const coverageId = isSelectByUm
			? `${product.corpSupplierCode}|${product.supplierProductNumber}|${product.unitMeasureCode}`
			: `${product.corpSupplierCode}|${product.supplierProductNumber}`;

		coverage[coverageId] = {
			additionalCoverageCount:
				dcsCoveredByProduct?.filter((dc) => !coveredDcs.has(dc)).length || 0,
			coverageFromThisProduct: dcsCoveredByProduct?.length || 0,
			coveredDCs: dcsCoveredByProduct || [],
			totalDcCount: allDcs.length || 0,
		};
	});
	return coverage;
}

function getCoveredDCsForProduct(
	product: IProduct,
	productHeader: IProductHeader,
	coverageFilters: string[],
	isSelectByUm: boolean
): string[] {
	return filterDCCoverage(
		product,
		productHeader.coveredDistributionCenters,
		productHeader.remoteStockDcs,
		productHeader.demandStockDcs,
		productHeader.nonstockDcs,
		coverageFilters,
		isSelectByUm
	);
}

export function filterDCCoverage(
	product: IProduct,
	allCoveredDistributionCenters: DistributionCenterWithUMs[],
	allRemoteStockDcs: DistributionCenterWithUMs[],
	allDemandStockDcs: DistributionCenterWithUMs[],
	allNonStockDcs: DistributionCenterWithUMs[],
	coverageFilters: string[],
	isSelectByUm: boolean
): string[] {
	if (isSelectByUm) {
		const allDcDssiCodes =
			allCoveredDistributionCenters == null
				? []
				: allCoveredDistributionCenters
						.filter((x) => x.unitMeasureCodes.includes(product.unitMeasureCode))
						.map((x) => x.distributionCenter);

		const nonStockSelected = coverageFilters.includes('N');
		const demandSelected = coverageFilters.includes('D');
		const remoteSelected = coverageFilters.includes('R');
		const stockedSelected = coverageFilters.includes('S');

		const remoteStockDcs =
			allRemoteStockDcs == null
				? []
				: allRemoteStockDcs
						.filter((x) => x.unitMeasureCodes.includes(product.unitMeasureCode))
						.map((x) => x.distributionCenter);
		const demandStockDcs =
			allDemandStockDcs == null
				? []
				: allDemandStockDcs
						.filter((x) => x.unitMeasureCodes.includes(product.unitMeasureCode))
						.map((x) => x.distributionCenter);
		const nonStockDcs =
			allNonStockDcs == null
				? []
				: allNonStockDcs
						.filter((x) => x.unitMeasureCodes.includes(product.unitMeasureCode))
						.map((x) => x.distributionCenter);

		const dcsThatAreNotStocked = remoteStockDcs
			.concat(demandStockDcs)
			.concat(nonStockDcs);

		const stockedDcsToFilterOut = stockedSelected
			? []
			: allDcDssiCodes.filter((dc) => dcsThatAreNotStocked.indexOf(dc) === -1);

		const filteredCoveredDcs = stockedDcsToFilterOut
			.concat(remoteSelected ? [] : remoteStockDcs)
			.concat(demandSelected ? [] : demandStockDcs)
			.concat(nonStockSelected ? [] : nonStockDcs);

		return allDcDssiCodes.filter((dc) => filteredCoveredDcs.indexOf(dc) === -1);
	} else {
		const allDcs =
			allCoveredDistributionCenters == null
				? []
				: allCoveredDistributionCenters.map((x) => x.distributionCenter);
		const nonStockSelected = coverageFilters.includes('N');
		const demandSelected = coverageFilters.includes('D');
		const remoteSelected = coverageFilters.includes('R');
		const stockedSelected = coverageFilters.includes('S');

		const remoteStockDcs =
			allRemoteStockDcs == null
				? []
				: allRemoteStockDcs.map((x) => x.distributionCenter);
		const demandStockDcs =
			allDemandStockDcs == null
				? []
				: allDemandStockDcs.map((x) => x.distributionCenter);
		const nonStockDcs =
			allNonStockDcs == null
				? []
				: allNonStockDcs.map((x) => x.distributionCenter);

		const dcsThatAreNotStocked = remoteStockDcs
			.concat(demandStockDcs)
			.concat(nonStockDcs);

		const stockedDcsToFilterOut = stockedSelected
			? []
			: allDcs.filter((dc) => dcsThatAreNotStocked.indexOf(dc) === -1);

		const filteredCoveredDcs = stockedDcsToFilterOut
			.concat(remoteSelected ? [] : remoteStockDcs)
			.concat(demandSelected ? [] : demandStockDcs)
			.concat(nonStockSelected ? [] : nonStockDcs);

		return allDcs.filter((dc) => filteredCoveredDcs.indexOf(dc) === -1);
	}
}

function getCoveredDCsForProductWithExclusions(
	product: IProduct,
	productHeader: IProductHeader,
	coverageFilters: string[],
	allDcs: IDistributionCenter[],
	dcExcludes: IMetaProductDistributionCenterOverride[],
	isSelectByUm: boolean
): string[] {
	if (isSelectByUm) {
		const allDcDssiCodes =
			productHeader.coveredDistributionCenters == null
				? []
				: allDcs
						.filter(
							(dc) =>
								productHeader.coveredDistributionCenters.findIndex(
									(x) =>
										x.distributionCenter === dc.dssiCode &&
										x.unitMeasureCodes.includes(product.unitMeasureCode)
								) > -1
						)
						.map((x) => x.dssiCode);

		const nonStockSelected = coverageFilters.includes('N');
		const demandSelected = coverageFilters.includes('D');
		const remoteSelected = coverageFilters.includes('R');
		const stockedSelected = coverageFilters.includes('S');

		const excludedDcIds = dcExcludes
			.filter((x) => x.unitMeasureCode === product.unitMeasureCode)
			.map((x) => x.distributionCenterId);

		const remoteStockDcs =
			productHeader.remoteStockDcs == null
				? []
				: productHeader.remoteStockDcs
						.filter((x) => x.unitMeasureCodes.includes(product.unitMeasureCode))
						.map((x) => x.distributionCenter);
		const demandStockDcs =
			productHeader.demandStockDcs == null
				? []
				: productHeader.demandStockDcs
						.filter((x) => x.unitMeasureCodes.includes(product.unitMeasureCode))
						.map((x) => x.distributionCenter);
		const nonStockDcs =
			productHeader.nonstockDcs == null
				? []
				: productHeader.nonstockDcs
						.filter((x) => x.unitMeasureCodes.includes(product.unitMeasureCode))
						.map((x) => x.distributionCenter);

		const dcsThatAreNotStocked = remoteStockDcs
			.concat(demandStockDcs)
			.concat(nonStockDcs);

		const stockedDcsToFilterOut = stockedSelected
			? []
			: allDcDssiCodes.filter((dc) => dcsThatAreNotStocked.indexOf(dc) === -1);

		const excludedDcs = allDcs
			.filter((dc) => excludedDcIds.includes(dc.id))
			.map((dc) => allDcDssiCodes.find((x) => x === dc.dssiCode));

		const filteredCoveredDcs = stockedDcsToFilterOut
			.concat(remoteSelected ? [] : remoteStockDcs)
			.concat(demandSelected ? [] : demandStockDcs)
			.concat(nonStockSelected ? [] : nonStockDcs)
			.concat(excludedDcs);

		return allDcDssiCodes.filter((dc) => filteredCoveredDcs.indexOf(dc) === -1);
	} else {
		allDcs =
			productHeader.coveredDistributionCenters == null
				? []
				: allDcs.filter(
						(dc) =>
							productHeader.coveredDistributionCenters.findIndex(
								(x) => x.distributionCenter === dc.dssiCode
							) > -1
				  );
		const allDcDssiCodes = allDcs.map((dc) => dc.dssiCode);
		const nonStockSelected = coverageFilters.includes('N');
		const demandSelected = coverageFilters.includes('D');
		const remoteSelected = coverageFilters.includes('R');
		const stockedSelected = coverageFilters.includes('S');
		const excludedDcIds = [];
		dcExcludes.forEach((dcExclude) =>
			excludedDcIds.push(dcExclude.distributionCenterId)
		);

		const remoteStockDcs =
			productHeader.remoteStockDcs == null
				? []
				: productHeader.remoteStockDcs.map((x) => x.distributionCenter);
		const demandStockDcs =
			productHeader.demandStockDcs == null
				? []
				: productHeader.demandStockDcs.map((x) => x.distributionCenter);
		const nonStockDcs =
			productHeader.nonstockDcs == null
				? []
				: productHeader.nonstockDcs.map((x) => x.distributionCenter);

		const dcsThatAreNotStocked = remoteStockDcs
			.concat(demandStockDcs)
			.concat(nonStockDcs);

		const stockedDcsToFilterOut = stockedSelected
			? []
			: allDcDssiCodes.filter((dc) => dcsThatAreNotStocked.indexOf(dc) === -1);

		const excludedDcs = allDcs
			.filter((dc) => excludedDcIds.includes(dc.id))
			.map((dc) => dc.dssiCode);

		const filteredCoveredDcs = stockedDcsToFilterOut
			.concat(remoteSelected ? [] : remoteStockDcs)
			.concat(demandSelected ? [] : demandStockDcs)
			.concat(nonStockSelected ? [] : nonStockDcs)
			.concat(excludedDcs);

		return allDcDssiCodes.filter((dc) => filteredCoveredDcs.indexOf(dc) === -1);
	}
}

function getMetaProductDistributionCenterPriorityOverrideIndex(
	distributionCenterPriorityOverrides: IMetaProductDistributionCenterOverride[],
	override: IMetaProductDistributionCenterOverride
): number {
	return (
		distributionCenterPriorityOverrides?.findIndex(
			(x) =>
				x.distributionCenterId === override.distributionCenterId &&
				x.supplierProductNumber === override.supplierProductNumber &&
				x.unitMeasureCode === override.unitMeasureCode &&
				x.overrideType === override.overrideType
		) || -1
	);
}

export const calculateTotalCoverage = createSelector(
	[
		getSelectedProducts,
		getSelectedProductHeaders,
		getDcs,
		getCoverageFilters,
		getProductPriorityOverrides,
		getSelectByUm,
	],
	(
		products: IProduct[],
		productHeaders: IProductHeader[],
		dcs: IDistributionCenter[],
		coverageFilters: string[],
		dcPriorityOverrides: IMetaProductDistributionCenterOverride[],
		isSelectByUm: boolean
	) => {
		let coverage = 0;
		let total = 0;
		const coveredDcs = new Set<string>();
		const dcDssiCodes = dcs.map((dc) => dc.dssiCode);
		if (dcs != null) {
			total = dcs.length;
		}
		if (products != null && total !== 0) {
			products.forEach((product) => {
				const productHeader = productHeaders.find(
					(x) =>
						x.corpSupplierCode === product.corpSupplierCode &&
						x.supplierProductNumber === product.supplierProductNumber
				);
				const productDcs = getCoveredDCsForProductWithExclusions(
					product,
					productHeader,
					coverageFilters,
					dcs,
					dcPriorityOverrides.filter(
						(dcpo) =>
							dcpo.supplierProductNumber === product.supplierProductNumber &&
							dcpo.overrideType ===
								IMetaProductDistributionCenterOverrideType.Exclude
					),
					isSelectByUm
				).filter((dc) => dcDssiCodes.includes(dc));
				productDcs.forEach((dc) => {
					coveredDcs.add(dc);
				});
			});
			coverage = coveredDcs.size;
		}
		return `${coverage}/${total}`;
	}
);

export const getUnCoveredDcs = createSelector(
	[
		getSelectedProducts,
		getSelectedProductHeaders,
		getDcs,
		getCoverageFilters,
		getProductPriorityOverrides,
		getSelectByUm,
	],
	(
		products: IProduct[],
		productHeaders: IProductHeader[],
		dcs: IDistributionCenter[],
		coverageFilters: string[],
		dcPriorityOverrides: IMetaProductDistributionCenterOverride[],
		isSelectByUm: boolean
	) => {
		const coveredDcs = new Set<string>();
		const dcDssiCodes = dcs.map((dc) => dc.dssiCode);
		if (products != null && dcs != null && dcs.length !== 0) {
			products.forEach((product) => {
				const productHeader = productHeaders.find(
					(x) =>
						x.corpSupplierCode === product.corpSupplierCode &&
						x.supplierProductNumber === product.supplierProductNumber
				);
				const productDcs = getCoveredDCsForProductWithExclusions(
					product,
					productHeader,
					coverageFilters,
					dcs,
					dcPriorityOverrides.filter(
						(dcpo) =>
							dcpo.supplierProductNumber === product.supplierProductNumber &&
							dcpo.overrideType ===
								IMetaProductDistributionCenterOverrideType.Exclude
					),
					isSelectByUm
				).filter((dc) => dcDssiCodes.includes(dc));
				productDcs.forEach((dc) => {
					coveredDcs.add(dc);
				});
			});
		}
		return dcDssiCodes.filter((dc) => !coveredDcs.has(dc));
	}
);

export const makeAvailableProductSelector = () => {
	return createSelector(
		[
			getProductHeaders,
			getProductAvailability,
			getSelectedLocationCode,
			getBlueprintId,
		],
		(products, productAvailability, locationCode, blueprintId) => {
			const availableProducts = [];
			if (locationCode == null || productAvailability == null) {
				return availableProducts;
			}

			products.forEach((product) => {
				const key = [
					blueprintId,
					locationCode,
					product.supplierProductNumber,
				].join('|');
				if (productAvailability[key]?.isAvailable || false) {
					availableProducts.push(productHeaderId(product));
				}
			});

			return availableProducts;
		}
	) as (state: IAppState, ids: IProduct[]) => string[];
};

export const copyRowValues = (
	productHeaderPrices: Dictionary<ProductHeaderPrices>,
	productHeaders: Dictionary<IProductHeader>,
	product: IProduct
) => {
	const key = `${product.corpSupplierCode}|${product.supplierProductNumber}`;

	const productHeader = productHeaders[key];

	let copyString =
		product.supplierProductNumber +
		'\t' +
		productHeader.description +
		'\t' +
		(productHeader.brand ?? '') +
		'\t' +
		productHeader.packSize +
		'\t';

	let numPrices = 0;
	let sortedArray = [];

	if (
		productHeaderPrices[key] === undefined ||
		productHeaderPrices[key].prices.length === 0 ||
		productHeaderPrices[key].prices.every((price) => price.max === null)
	) {
		sortedArray = productHeader.unitMeasureCodes
			.filter((UM) => UM !== null)
			.sort((a, b) => a.localeCompare(b));
		sortedArray.forEach((um) => {
			copyString += um + '\t\t\n\t\t\t\t';
		});
	} else {
		sortedArray = productHeaderPrices[key].prices
			.filter((price) => price.unitOfMeasure !== null)
			.sort((a, b) => a.unitOfMeasure.localeCompare(b.unitOfMeasure));
		sortedArray.forEach((price) => {
			const bothPricePerUnitNotNullAndNotEqual =
				price.minPerUnit &&
				price.maxPerUnit &&
				price.minPerUnit !== price.maxPerUnit;
			const bothPricesNotNullAndNotEqual =
				price.min && price.max && price.min !== price.max;
			if (price.max !== null) {
				const addedString =
					'\n' +
					'\t\t\t\t' +
					price.unitOfMeasure +
					'\t' +
					(bothPricesNotNullAndNotEqual ? '$' + price.min + '-' : '') +
					(price.max ? '$' + price.max : '') +
					'\t' +
					(bothPricePerUnitNotNullAndNotEqual
						? '$' + price.minPerUnit + '-'
						: '') +
					(price.maxPerUnit ? '$' + price.maxPerUnit : '') +
					(price.minPerUnit || price.maxPerUnit ? ' per ' : '') +
					(price.perUnit ? price.perUnit : '') +
					'\t';
				if (numPrices === 0) {
					copyString +=
						(price.unitOfMeasure ? price.unitOfMeasure : '') +
						'\t' +
						(bothPricesNotNullAndNotEqual ? '$' + price.min + '-' : '') +
						(price.max ? '$' + price.max : '') +
						'\t' +
						(bothPricePerUnitNotNullAndNotEqual
							? '$' + price.minPerUnit + '-'
							: '') +
						(price.maxPerUnit ? '$' + price.maxPerUnit : '') +
						(price.minPerUnit || price.maxPerUnit ? ' per ' : '') +
						(price.perUnit ? price.perUnit : '') +
						'\t';
					numPrices += 1;
				} else {
					copyString += addedString;
				}
			}
		});
	}
	navigator.clipboard.writeText(copyString);
};
